import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
  LinearProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import PrintIcon from "@mui/icons-material/Print";
import { toast } from "react-toastify";
import DEVICE_TYPE from "../../enum/device_type";
import LOCATION_TYPE_ID from "../../enum/location_type";
import CollectDeliveryDialog from "../../collect-delivery/CollectDeliveryDialog";
import {
  COLLECT_DELIVERY_MESSAGES,
  collectOrderById,
  collectOrderByStock,
  confirmCollectDelivery as submitCollectDelivery,
  fetchDeliveryUsers,
  findUserByBadgeId,
  getCollectDeliveryLocationLabel,
  getDeliveryUserLabel,
  isCollectDeliveryLocationType,
  isOrderInCart,
  lookupDeliveryUserByBadgeId,
  normalizeBadgeId,
} from "../../../utils/collectDelivery";
import ArrivedNotification from "../../console/notificationsound";
import styles from "../index.styles";
import CancelDialog from "./dialogs/CancelDialog";
import PrintDialog from "./dialogs/PrintDialog";
import MultiPickupDialog from "./dialogs/MultiPickupDialog";
import AddToOrderDialog from "./dialogs/AddToOrderDialog";
import {
  compareOrders,
  getStatusText,
} from "./helpers/orderUtils";
import { checkOrderCompletion } from "./helpers/completionUtils";
import CounterOrdersList from "./components/CounterOrdersList";
import ScanSection from "./components/ScanSection";
import PatientInfoCard from "./components/PatientInfoCard";
import PrescriptionItemsTable from "./components/PrescriptionItemsTable";
import CounterSearchInput from "./components/CounterSearchInput";
import DeliverySummaryPanel from "./components/DeliverySummaryPanel";
import PatientVerificationDialog from "./components/PatientVerificationDialog";
import VideoConsultation from "./components/VideoConsultation";
import NonRxCart from "../NonRxCart";
import MarketplaceUpcScan from "../MarketplaceUpcScan";
import OtcDeliveryCallout from "../../features/OtcDeliveryCallout";
import { writeAuditLog } from "../../common/apis";
import SocketService from "./services/SocketService";
import OrderService from "./services/OrderService";
import ConsultationService from "./services/ConsultationService";
import DateUtils from "./utils/DateUtils";
import SearchUtils from "./utils/SearchUtils";
import DeliveryTypeTabs from "../DeliveryTypeTabs";
import CONNECTION_TYPE from "../../common/connection-type";

class CounterAssignUI extends Component {
  constructor(props) {
    super(props);

    const initialPageSize = 50;
    const initialRowPerPage = 10;

    this.state = {
      lastConsoleSnapshot: null,
      counterOrders: [], // Counter orders fetched directly in this component
      loadingCounterOrders: false,
      // Order details dialog state
      open: false,
      popupData: "",
      loader: false,
      type_id: "",
      id: "",
      customer_id: "",
      box_id: "",
      pucode: "",
      fromType: "",
      selectedTypeId: LOCATION_TYPE_ID.Counter,
      deletepopup: false,
      printDialogOpen: false,
      isPatientVerificationOpen: false,
      patientVerifyName: "",
      patientVerifyDob: "",
      patientVerifyError: "",
      isSubmittingPatientVerification: false,
      pendingVerificationOrder: null, // Order to open after verification (from P/U Code click)
      // Pagination state for InfiniteScroll
      rowPerPageCounter: initialRowPerPage,
      hasMoreCounter: true,
      counterDataTotal: 0,
      loadingMoreCounter: false,
      // Search state
      searchText: "",
      lastSearchText: "",
      pendingCounterShortcutSearch: null,
      // Payment status toggle state
      isUpdatingPaymentStatus: false,
      paymentStatusError: "",
      // Terminal payment processing state
      isProcessingTerminalPayment: false,
      // Pay Later (payroll deduct) processing state
      isProcessingPayLater: false,
      // Cash payment processing state
      isProcessingCashPayment: false,
      // Strict patient verification flag from location data
      strictPatientVerification: false,
      // Enable trim (from location enable_trim + trim_chars)
      enableTrim: false,
      trimChars: SearchUtils.defaultTrimChars,
      // Track how order details dialog was opened
      dialogOpenSource: null, // "click", "qr_scan", "verify_patient", or null
      // Location validation state
      locationError: "",
      locationTouched: false,
      validatedLocation: null, // Location object from validation API
      isValidatingLocation: false, // Loading state during validation
      // Consultation state
      consultationNotes: "",
      isSavingConsultation: false,
      isDecliningConsultation: false,
      consultationError: "",
      // ID scan state
      isScanningId: false,
      scanIdError: "",
      // Multiple orders for same patient
      patientOrders: [], // All orders for the current patient
      // Customer info for dialog
      dialogCustomerName: "",
      dialogCustomerDob: "",
      dialogCustomerPhone: "",
      dialogCustomerEmail: "",
      // Order completion tracking
      orderCompletionStatus: {}, // Map of orderId -> completion status
      orderFullDetailsMap: {}, // Map of orderId -> full order details (cached)
      // Bag scan status tracking
      orderBagScanStatus: {}, // Map of orderId -> { isScanned: boolean, isVerified: boolean, scannedValue: string }
      // Video consultation state
      isConsult: false,
      isVideoConsultation: false,
      isConsultAcceptedCall: false,
      isPharmacistConnected: false,
      isAudioOnlyConsult: false,
      startRinging: false,
      connectedPharmacistName: "",
      localVideoTrack: null,
      remoteVideoTrack: null,
      consultPuCode: null,
      consultUserId: null,
      isConsultNotAvailable: false,
      isReleasedModal: false,
      isDisable: false,
      videoConsultationError: "",
      isStartingConsultation: false, // Loading state for consult button
      // Add to existing order (Ready bag) dialog
      addToDialogOpen: false,
      addToSelectedOrderId: null,
      addToSubmitting: false,
      scanInputResetKey: 0,
      collectDeliveryOpen: false,
      collectDeliveryUsers: [],
      collectDeliveryUserId: "",
      collectDeliveryBadge: "",
      collectDeliveryStockCode: "",
      collectDeliveryCart: [],
      collectDeliveryError: "",
      collectDeliveryLoading: false,
      collectDeliveryConfirming: false,
      collectDeliveryLocationTypeId: "",
    };
    this.scanRxInputRef = React.createRef();
    this.saveButtonRef = React.createRef();
    this.socket = null;
    // Initialize services
    this.socketService = new SocketService(this);
    this.orderService = new OrderService(this);
    this.consultationService = new ConsultationService(this);

    this.consoleRequest = {
      pageNo: {
        counter: 1,
        bedside: 1,
        curbside: 1,
        courier: 1,
      },
      pageSize: {
        counter: initialPageSize,
        bedside: initialPageSize,
        curbside: initialPageSize,
        courier: initialPageSize,
      },
      scanText: "",
      searchText: "",
      sortColumn: "created_on",
      sortOrder: "desc",
      isKaktus: false,
      location_id: (props.currentBoxData && props.currentBoxData.id) || null,
    };
  }

  getActiveConsoleKey = () => {
    if (this.props.locationtype_id === LOCATION_TYPE_ID.Bedside) return "bedside";
    if (this.props.locationtype_id === LOCATION_TYPE_ID.Curbside) return "curbside";
    if (this.props.locationtype_id === LOCATION_TYPE_ID.Courier) return "courier";
    return "counter";
  };

  getDeliveryConsoleEndpoint = () =>
    `deliveryconsole/${this.getActiveConsoleKey()}`;

  syncDefaultValidatedLocation = () => {
    const defaultLocation = this.props.defaultValidatedLocation;
    const binId = (this.props.binId || "").trim().toLowerCase();

    if (!defaultLocation || !binId) {
      return;
    }

    const location = (defaultLocation.location || "").trim().toLowerCase();
    const locationCode = (defaultLocation.location_code || "").trim().toLowerCase();
    const matchesDefault = binId === location || binId === locationCode;

    if (!matchesDefault) {
      return;
    }

    const currentLocation = this.state.validatedLocation;
    if (currentLocation && String(currentLocation.id) === String(defaultLocation.id)) {
      return;
    }

    this.setState({
      validatedLocation: defaultLocation,
      locationError: "",
      locationTouched: false,
      isValidatingLocation: false,
    });
  };

  componentDidMount() {
    // Auto-focus the scan RX input when component mounts
    if (this.scanRxInputRef.current) {
      this.scanRxInputRef.current.focus();
    }
    this.props.onGetAccountConnection && this.props.onGetAccountConnection();
    // Extract strict_patient_verification, enable_trim, trim_chars from currentBoxData prop
    const currentBoxData = this.props.currentBoxData;
    if (currentBoxData) {
      const updates = {};
      if (typeof currentBoxData.strict_patient_verification === "boolean") {
        updates.strictPatientVerification = currentBoxData.strict_patient_verification;
      }
      if (typeof currentBoxData.enable_trim === "boolean") {
        updates.enableTrim = currentBoxData.enable_trim;
      }
      if (typeof currentBoxData.trim_chars === "string") {
        updates.trimChars = currentBoxData.trim_chars;
      }
      if (Object.keys(updates).length > 0) {
        this.setState(updates);
      }
    }
    // Initialize socket and fetch counter orders
    // Use getConsole like Console does to get consistent data
    this.socketService.init();
    // Socket reference is set by SocketService.init()
    if (this.socket) {
      this.socket.emit("getConsole", this.consoleRequest);
      this.setState({ loadingCounterOrders: true });
    }
    this.syncDefaultValidatedLocation();
  }

  componentWillUnmount() {
    // Clean up socket listeners to prevent memory leaks
    this.socketService.cleanup();
    this.socket = null;

    // Clean up consultation service
    if (this.consultationService) {
      this.consultationService.cleanup();
    }
  }


  // Play arrival notification sound (called when socket emits "counterOrderArrived")
  playArrivalSound = () => {
    let sound = document.getElementById("curbSound");
    if (sound) {
      sound.play().catch((err) => console.warn("Order arrival sound play failed:", err));
    }
  };

  // Fetch more counter orders for InfiniteScroll pagination
  fetchMoreCounterData = () => {
    const { counterOrders, counterDataTotal, loadingMoreCounter, rowPerPageCounter } = this.state;

    // Prevent multiple simultaneous requests
    if (loadingMoreCounter) {
      return;
    }

    let total = 0;
    total =
      counterOrders &&
      counterOrders.length;
    if (
      total > 0 &&
      counterOrders.length < counterDataTotal
    ) {
      const increment = 10;
      this.setState(
        { rowPerPageCounter: rowPerPageCounter + increment, loadingMoreCounter: true },
        () => {
          const activeConsoleKey = this.getActiveConsoleKey();
          this.consoleRequest.pageSize[activeConsoleKey] = this.state.rowPerPageCounter;
          if (this.socket) {
            this.socket.emit(
              activeConsoleKey === "counter" ? "getCounter" : "getConsole",
              this.consoleRequest
            );
          } else {
            this.fetchCounterOrders();
          }
        }
      );
    } else {
      this.setState({ hasMoreCounter: false });
    }
  };

  fetchCounterOrders = () => {
    this.setState({ loadingCounterOrders: true });
    const data = {
      pageNo: 1,
      pageSize: 50,
      sortColumn: "created_on",
      sortOrder: "desc",
      searchText: "",
      scanText: "",
    };
    fetch(process.env.REACT_APP_BASEURL + this.getDeliveryConsoleEndpoint(), {
      method: "POST",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res && res.results && res.results.length > 0) {
          this.setState({
            counterOrders: res.results,
            loadingCounterOrders: false,
          });
        } else {
          this.setState({ counterOrders: [], loadingCounterOrders: false });
        }
      })
      .catch((error) => {
        console.error("Error fetching counter orders:", error);
        this.setState({ counterOrders: [], loadingCounterOrders: false });
      });
  };

  handleOrderClick = (order) => {
    if (this.state.collectDeliveryOpen) {
      this.addCollectDeliveryOrder(order);
      return;
    }

    // In strict mode, open verification dialog instead of directly opening order
    if (this.state.strictPatientVerification) {
      this.openPatientVerificationDialog(order);
      return;
    }

    // Track that dialog is being opened via click
    this.handleOpen("counter", order, "click");
  };

  // Handle switching to a different order from the Collect panel
  handleOrderSwitch = (order) => {
    if (!order || !order.id) {
      return;
    }

    // Update selectedOrderId to trigger scroll in MultiPickupDialog
    if (this.state.open) {
      this.setState({ id: order.id, selectedOrderId: order.id });
      return;
    }

    // If dialog is not open, open it with the selected order
    this.handleOpen(order.DeliveryType || "counter", order, "collect_switch");
  };

  parsePickupShortcut = (value) => {
    const trimmedValue = (value || "").trim();
    if (!trimmedValue) {
      return null;
    }

    const match = trimmedValue.match(/^(counter|bedside)\s*[-:]\s*(.+)$/i);
    if (!match || !match[1] || !match[2] || !match[2].trim().length) {
      return null;
    }

    const shortcutType = match[1].trim().toLowerCase();
    return {
      type: shortcutType,
      consoleKey: shortcutType === "bedside" ? "bedside" : "counter",
      pucode: match[2].trim().toLowerCase(),
    };
  };

  handleScanChange = (value) => {
    const trimmedValue = (value || "").trim();
    const shortcut = this.parsePickupShortcut(trimmedValue);

    if (this.state.strictPatientVerification && !shortcut) {
      this.props.onRxScan && this.props.onRxScan(trimmedValue);
      return;
    }

    const handled = this.handlePickupShortcut(trimmedValue, { source: "scan" });
    if (!handled) {
      this.props.onRxScan && this.props.onRxScan(trimmedValue);
    }
  };

  handlePickupShortcut = (value, { source } = {}) => {
    const shortcut = this.parsePickupShortcut(value);
    const { onToggleLoader } = this.props;

    if (!shortcut) {
      return false;
    }

    const searchPuCode = shortcut.pucode;
    const shortcutConsoleKey = shortcut.consoleKey;

    const collections = this.buildDeliveryCollections();
    if (collections.length > 0) {
      const { data } = collections[0];
      const results = (data && data.results) || [];
      const matchedOrder = results.find(
        (order) =>
          order && order.pucode && order.pucode.toLowerCase() === searchPuCode
      );

      if (matchedOrder) {
        const nextState = {
          loader: true,
          ...(source === "search" ? { searchText: "" } : {}),
        };

        onToggleLoader && onToggleLoader(true);
        this.setState(nextState, () => {
          if (source === "scan") {
            this.props.onRxScan && this.props.onRxScan("");
          }
          this.handleOpen(shortcutConsoleKey, matchedOrder, "qr_scan");
        });
        return true;
      }
    }

    if (this.socket) {
      this.setState({
        pendingCounterShortcutSearch: {
          pucode: searchPuCode,
          consoleKey: shortcutConsoleKey,
          source: source || "search",
        },
      });

      const fallbackRequest = {
        ...this.consoleRequest,
        searchText: searchPuCode,
      };

      this.socket.emit("getConsole", fallbackRequest);
      onToggleLoader && onToggleLoader(true);
      return true;
    }

    onToggleLoader && onToggleLoader(false);
    return false;
  };

  buildDeliveryCollections = () => {
    const collections = [];
    const snapshot = this.state.lastConsoleSnapshot || {};

    if (this.state.counterOrders && this.state.counterOrders.length > 0) {
      collections.push({ type: "counter", data: { results: this.state.counterOrders } });
    } else if (snapshot.counter) {
      collections.push({ type: "counter", data: snapshot.counter });
    }

    return collections;
  };

  // Search utility methods moved to SearchUtils class
  // Use SearchUtils.normalizeSearchValue(), SearchUtils.findOrdersMatchingSearch(), etc.

  handleOpen = (type, obj, source = null) => {
    const deliveryTypeLabel = obj.DeliveryType || type || "";
    let selectedTypeId = LOCATION_TYPE_ID.Counter;

    // Track how the dialog was opened
    const openSource = source || "click"; // Default to "click" if not specified

    // STRICT MODE CHECK: Prevent unauthorized dialog opens
    const isStrictMode = this.state.strictPatientVerification === true;
    // Allowed sources in strict mode:
    // - "qr_scan": QR code scanned (Counter-[pucode] or Bedside-[pucode])
    // - "verify_patient": Manual patient verification successful
    // - "collect_switch": Switching between orders when dialog is already open
    const allowedSourcesInStrictMode = ["qr_scan", "verify_patient", "collect_switch"];
    const isAllowedSource = allowedSourcesInStrictMode.includes(openSource);

    if (isStrictMode && !isAllowedSource) {
      // Block the dialog open in strict mode unless from allowed sources
      return;
    }

    switch (obj.locationtype_id) {
      case LOCATION_TYPE_ID.Kiosk:
        selectedTypeId = LOCATION_TYPE_ID.Kiosk;
        break;
      case LOCATION_TYPE_ID.Courier:
        selectedTypeId = LOCATION_TYPE_ID.Courier;
        break;
      case LOCATION_TYPE_ID.Curbside:
        selectedTypeId = LOCATION_TYPE_ID.Curbside;
        break;
      case LOCATION_TYPE_ID.Bedside:
        selectedTypeId = LOCATION_TYPE_ID.Bedside;
        break;
      case LOCATION_TYPE_ID.Counter:
      default:
        selectedTypeId = LOCATION_TYPE_ID.Counter;
        break;
    }

    const customerId = obj.customer_id;
    const customerName = obj.customerName || "";
    const customerDob = obj.dob_str || obj.dob || "";
    const customerPhone = obj.phone || "";
    const customerEmail = obj.email || "";

    const sameCustomer = (order) =>
      order &&
      order.customer_id !== undefined &&
      order.customer_id !== null &&
      String(order.customer_id) === String(customerId);

    const patientOrdersFromCounter = (this.state.counterOrders || []).filter(sameCustomer);

    const currentPatientOrders = (this.state.patientOrders || []).filter(sameCustomer);
    let patientOrdersList = patientOrdersFromCounter.map((counterOrder) => {
      const localOrder = currentPatientOrders.find(
        (po) => po && po.id === counterOrder.id
      );
      return localOrder || counterOrder;
    });

    currentPatientOrders.forEach((localOrder) => {
      if (localOrder && !patientOrdersList.find(po => po && po.id === localOrder.id)) {
        patientOrdersList.push(localOrder);
      }
    });

    if (obj && obj.id && !patientOrdersList.find((po) => po && po.id === obj.id)) {
      patientOrdersList = [obj].concat(patientOrdersList);
    }

    const openingDifferentCustomer =
      String(this.state.customer_id || "") !== String(customerId || "");

    // Set initial state with loader true and open dialog immediately
    this.setState({
      type_id: obj.type_id,
      fromType: deliveryTypeLabel,
      selectedTypeId,
      id: obj.id,
      customer_id: customerId,
      box_id: obj.box_id,
      pucode: obj.pucode,
      loader: true,
      dialogOpenSource: openSource,
      patientOrders: patientOrdersList,
      orderBagScanStatus: openingDifferentCustomer ? {} : this.state.orderBagScanStatus,
      open: true, // Open dialog immediately to show skeleton loader
      popupData: null, // Set to null initially so skeleton shows
      dialogCustomerName: customerName,
      dialogCustomerDob: customerDob,
      dialogCustomerPhone: customerPhone,
      dialogCustomerEmail: customerEmail,
    }, () => {
      this.props.onToggleLoader && this.props.onToggleLoader(true);
    });

    // Track promises for all async operations
    const promises = [];

    // Fetch main order details using batch API
    const mainOrderPromise = this.orderService.getOrderDetailsBatch([obj.id])
      .then((batchResult) => {
        if (!batchResult.orders || batchResult.orders.length === 0) {
          // Log error if fetch failed
          if (batchResult.errors && batchResult.errors.length > 0) {
            console.error(`Error fetching order ${obj.id}:`, batchResult.errors[0].message);
          }
          throw new Error("Failed to fetch order details");
        }

        const res = batchResult.orders[0];

        let existingNotes = "";
        if (res && res.consultationDetails && Array.isArray(res.consultationDetails) && res.consultationDetails.length > 0) {
          const latestConsultation = res.consultationDetails[res.consultationDetails.length - 1];
          existingNotes = latestConsultation.consultation_notes || "";
        }

        // Check completion status for this order (with shared signature/ID from all patient orders)
        const bagScanStatus = this.state.orderBagScanStatus[obj.id] || null;
        const completionStatus = checkOrderCompletion(
          obj,
          res,
          patientOrdersList,
          this.state.orderFullDetailsMap,
          bagScanStatus
        );

        // Update completion status and cache full details
        this.setState(prevState => ({
          popupData: res,
          consultationNotes: existingNotes,
          consultationError: "",
          orderCompletionStatus: {
            ...prevState.orderCompletionStatus,
            [obj.id]: completionStatus,
          },
          orderFullDetailsMap: {
            ...prevState.orderFullDetailsMap,
            [obj.id]: res,
          },
        }));

        return { res };
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
        throw error;
      });

    promises.push(mainOrderPromise);

    // Fetch all customer orders
    const customerOrdersPromise = this.fetchAllCustomerOrdersPromise(customerId, customerName, patientOrdersList);
    if (customerOrdersPromise) {
      promises.push(customerOrdersPromise);
    }

    // Wait for all promises to complete
    Promise.all(promises)
      .then((results) => {
        // All data is loaded, turn off loader
        this.setState({ loader: false }, () => {
          this.props.onToggleLoader && this.props.onToggleLoader(false);
        });
      })
      .catch((error) => {
        console.error("Error in data fetching:", error);
        this.setState({ loader: false }, () => {
          this.props.onToggleLoader && this.props.onToggleLoader(false);
        });
      });
  };

  fetchAllCustomerOrders = (customerId, customerName = "", existingPatientOrders = []) => {
    if (!customerId) {
      return;
    }

    // Use customer name in searchText for more targeted server-side filtering
    // Still filter by customer_id client-side for accuracy
    const data = {
      pageNo: 1,
      pageSize: 500,
      sortColumn: "created_on",
      sortOrder: "desc",
      searchText: customerName || "",
      scanText: "",
    };

    fetch(process.env.REACT_APP_BASEURL + this.getDeliveryConsoleEndpoint(), {
      method: "POST",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res && res.results && res.results.length > 0) {
          const customerOrders = res.results.filter(
            (order) => order && String(order.customer_id) === String(customerId)
          );
          this.mergeCustomerOrdersIntoPatientOrders(customerId, customerOrders, existingPatientOrders);
        } else {
          this.mergeCustomerOrdersIntoPatientOrders(customerId, [], existingPatientOrders);
        }
      })
      .catch((error) => {
        console.error("Error fetching all customer orders:", error);
      });
  };

  fetchAllCustomerOrdersPromise = (customerId, customerName = "", existingPatientOrders = []) => {
    if (!customerId) {
      return Promise.resolve();
    }

    // Use customer name in searchText for more targeted server-side filtering
    // Still filter by customer_id client-side for accuracy
    const data = {
      pageNo: 1,
      pageSize: 500,
      sortColumn: "created_on",
      sortOrder: "desc",
      searchText: customerName || "",
      scanText: "",
    };

    return fetch(process.env.REACT_APP_BASEURL + this.getDeliveryConsoleEndpoint(), {
      method: "POST",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res && res.results && res.results.length > 0) {
          const customerOrders = res.results.filter(
            (order) => order && String(order.customer_id) === String(customerId)
          );
          return this.mergeCustomerOrdersIntoPatientOrdersPromise(customerId, customerOrders, existingPatientOrders);
        } else {
          return this.mergeCustomerOrdersIntoPatientOrdersPromise(customerId, [], existingPatientOrders);
        }
      })
      .catch((error) => {
        console.error("Error fetching all customer orders:", error);
        return Promise.resolve(); // Resolve to not block other promises
      });
  };

  mergeCustomerOrdersIntoPatientOrders = (customerId, fetchedOrders, existingPatientOrders = []) => {
    if (String(this.state.customer_id) !== String(customerId)) {
      return;
    }

    const mergedOrders = fetchedOrders.map((fetchedOrder) => {
      const existingOrder = existingPatientOrders.find(
        (po) => po && po.id === fetchedOrder.id
      );
      if (existingOrder) {
        return existingOrder;
      }

      const stateOrder = (this.state.patientOrders || []).find(
        (po) => po && po.id === fetchedOrder.id
      );
      if (stateOrder) {
        return stateOrder;
      }

      return fetchedOrder;
    });

    existingPatientOrders.forEach((existingOrder) => {
      if (
        existingOrder &&
        String(existingOrder.customer_id) === String(customerId) &&
        !mergedOrders.find(po => po && po.id === existingOrder.id)
      ) {
        mergedOrders.push(existingOrder);
      }
    });

    this.setState({ patientOrders: mergedOrders }, () => {
      // Fetch details for all orders to check completion status
      this.fetchCompletionStatusForAllOrders(mergedOrders);
    });
  };

  mergeCustomerOrdersIntoPatientOrdersPromise = (customerId, fetchedOrders, existingPatientOrders = []) => {
    if (String(this.state.customer_id) !== String(customerId)) {
      return Promise.resolve();
    }

    const mergedOrders = fetchedOrders.map((fetchedOrder) => {
      const existingOrder = existingPatientOrders.find(
        (po) => po && po.id === fetchedOrder.id
      );
      if (existingOrder) {
        return existingOrder;
      }

      const stateOrder = (this.state.patientOrders || []).find(
        (po) => po && po.id === fetchedOrder.id
      );
      if (stateOrder) {
        return stateOrder;
      }

      return fetchedOrder;
    });

    existingPatientOrders.forEach((existingOrder) => {
      if (
        existingOrder &&
        String(existingOrder.customer_id) === String(customerId) &&
        !mergedOrders.find(po => po && po.id === existingOrder.id)
      ) {
        mergedOrders.push(existingOrder);
      }
    });

    return new Promise((resolve) => {
      this.setState({ patientOrders: mergedOrders }, () => {
        // Fetch details for all orders to check completion status
        const completionPromise = this.fetchCompletionStatusForAllOrdersPromise(mergedOrders);
        if (completionPromise) {
          completionPromise.then(() => {
            resolve();
          }).catch(() => {
            resolve(); // Resolve anyway to not block
          });
        } else {
          resolve();
        }
      });
    });
  };

  // Fetch full details for all orders to check completion status
  fetchCompletionStatusForAllOrders = (orders = []) => {
    if (!orders || orders.length === 0) {
      return;
    }

    const ordersToFetch = orders.filter((order) => {
      if (!order || !order.id) return false;
      // Only fetch if we don't already have full details cached
      return !this.state.orderFullDetailsMap[order.id];
    });

    if (ordersToFetch.length === 0) {
      // All orders already have completion status, just recalculate from cached data
      this.updateCompletionStatusFromCachedData(orders);
      return;
    }

    // Use batch API to fetch all order details in a single request
    const orderIdsToFetch = ordersToFetch.map((order) => order.id);

    this.orderService.getOrderDetailsBatch(orderIdsToFetch)
      .then((batchResult) => {
        const newFullDetailsMap = { ...this.state.orderFullDetailsMap };

        if (batchResult.orders && batchResult.orders.length > 0) {
          batchResult.orders.forEach((orderData) => {
            if (orderData && orderData.id) {
              newFullDetailsMap[orderData.id] = orderData;
            }
          });
        }

        // Log any errors from batch fetch
        if (batchResult.errors && batchResult.errors.length > 0) {
          batchResult.errors.forEach((err) => {
            console.error(`Error fetching details for order ${err.orderId}: ${err.message}`);
          });
        }

        // Recalculate completion status for ALL orders with shared signature/ID
        const recalculatedCompletionStatus = {};
        orders.forEach((order) => {
          if (!order || !order.id) return;
          const fullDetails = newFullDetailsMap[order.id] || null;
          const bagScanStatus = this.state.orderBagScanStatus[order.id] || null;
          recalculatedCompletionStatus[order.id] = checkOrderCompletion(
            order,
            fullDetails,
            orders, // All patient orders for shared signature/ID
            newFullDetailsMap, // Updated full details map
            bagScanStatus
          );
        });

        this.setState({
          orderCompletionStatus: recalculatedCompletionStatus,
          orderFullDetailsMap: newFullDetailsMap,
        });
      })
      .catch((error) => {
        console.error("Error in batch fetch for completion status:", error);
        // Fallback: recalculate with whatever data we have
        this.updateCompletionStatusFromCachedData(orders);
      });
  };

  fetchCompletionStatusForAllOrdersPromise = (orders = []) => {
    if (!orders || orders.length === 0) {
      return Promise.resolve();
    }

    const ordersToFetch = orders.filter((order) => {
      if (!order || !order.id) return false;
      // Only fetch if we don't already have full details cached
      return !this.state.orderFullDetailsMap[order.id];
    });

    if (ordersToFetch.length === 0) {
      // All orders already have completion status, just recalculate from cached data
      this.updateCompletionStatusFromCachedData(orders);
      return Promise.resolve();
    }

    // Use batch API to fetch all order details in a single request
    const orderIdsToFetch = ordersToFetch.map((order) => order.id);

    return this.orderService.getOrderDetailsBatch(orderIdsToFetch)
      .then((batchResult) => {
        const newFullDetailsMap = { ...this.state.orderFullDetailsMap };

        // Update the full details map with fetched data
        if (batchResult.orders && batchResult.orders.length > 0) {
          batchResult.orders.forEach((orderData) => {
            if (orderData && orderData.id) {
              newFullDetailsMap[orderData.id] = orderData;
            }
          });
        }

        // Log any errors from batch fetch
        if (batchResult.errors && batchResult.errors.length > 0) {
          batchResult.errors.forEach((err) => {
            console.error(`Error fetching details for order ${err.orderId}: ${err.message}`);
          });
        }

        // Recalculate completion status for ALL orders with shared signature/ID
        const recalculatedCompletionStatus = {};
        orders.forEach((order) => {
          if (!order || !order.id) return;
          const fullDetails = newFullDetailsMap[order.id] || null;
          const bagScanStatus = this.state.orderBagScanStatus[order.id] || null;
          recalculatedCompletionStatus[order.id] = checkOrderCompletion(
            order,
            fullDetails,
            orders, // All patient orders for shared signature/ID
            newFullDetailsMap, // Updated full details map
            bagScanStatus
          );
        });

        this.setState({
          orderCompletionStatus: recalculatedCompletionStatus,
          orderFullDetailsMap: newFullDetailsMap,
        });
      })
      .catch((error) => {
        console.error("Error in batch fetch for completion status:", error);
        // Fallback: recalculate with whatever data we have
        this.updateCompletionStatusFromCachedData(orders);
      });
  };

  // Update completion status from cached full details
  updateCompletionStatusFromCachedData = (orders = []) => {
    const newCompletionStatus = { ...this.state.orderCompletionStatus };
    const currentFullDetailsMap = this.state.orderFullDetailsMap || {};

    orders.forEach((order) => {
      if (!order || !order.id) return;

      const fullDetails = currentFullDetailsMap[order.id] || null;
      // Check completion with shared signature/ID from all patient orders
      const bagScanStatus = this.state.orderBagScanStatus[order.id] || null;
      newCompletionStatus[order.id] = checkOrderCompletion(
        order,
        fullDetails,
        orders, // All patient orders
        currentFullDetailsMap,
        bagScanStatus
      );
    });

    this.setState({ orderCompletionStatus: newCompletionStatus });
  };

  handleClose = () => {
    // Reset bag scan status and completion status for ALL patient orders when dialog closes
    // This ensures that when the dialog reopens, status is fresh and recalculated
    const patientOrders = this.state.patientOrders || [];
    const currentOrderId = this.state.popupData && this.state.popupData.id;
    const newBagScanStatus = { ...this.state.orderBagScanStatus };
    const newCompletionStatus = { ...this.state.orderCompletionStatus };

    // Clear bag scan status and completion status for all patient orders
    patientOrders.forEach((order) => {
      if (order && order.id) {
        delete newBagScanStatus[order.id];
        delete newCompletionStatus[order.id];
      }
    });

    // Also clear current order if it's not in patientOrders (safety check)
    if (currentOrderId && !patientOrders.find(o => o && o.id === currentOrderId)) {
      delete newBagScanStatus[currentOrderId];
      delete newCompletionStatus[currentOrderId];
    }

    this.setState({
      open: false,
      lastSearchText: "",
      searchText: "",
      dialogOpenSource: null,
      consultationNotes: "",
      consultationError: "",
      patientOrders: [],
      orderBagScanStatus: newBagScanStatus,
      orderCompletionStatus: newCompletionStatus,
      dialogCustomerName: "",
      dialogCustomerDob: "",
      dialogCustomerPhone: "",
      dialogCustomerEmail: "",
      scanInputResetKey: this.state.scanInputResetKey + 1,
    }, () => {
      this.consoleRequest.searchText = "";
      if (this.socket) {
        this.socket.emit("getConsole", this.consoleRequest);
      }
      if (this.scanRxInputRef.current) {
        setTimeout(() => {
          if (this.scanRxInputRef.current) {
            this.scanRxInputRef.current.focus();
          }
        }, 100);
      }
    });
  };

  // Update bag scan status for an order
  updateBagScanStatus = (orderId, scanStatus) => {
    if (!orderId) return;

    this.setState(prevState => ({
      orderBagScanStatus: {
        ...prevState.orderBagScanStatus,
        [orderId]: {
          ...prevState.orderBagScanStatus[orderId],
          ...scanStatus,
        },
      },
    }), () => {
      // Recalculate completion status after bag scan status update
      if (this.state.patientOrders && this.state.patientOrders.length > 0) {
        this.updateCompletionStatusFromCachedData(this.state.patientOrders);
      }
    });
  };

  inputHandler = (type, value) => {
    if (type === "searchText") {
      // Value is already trimmed by CounterSearchInput component
      const normalized = value === null || value === undefined ? "" : String(value);
      const handled = this.handlePickupShortcut(normalized, { source: "search" });
      if (handled) {
        this.consoleRequest.searchText = "";
        return;
      }
      this.setState({ [type]: normalized }, () => {
        if (
          this.state.searchText.length > 1 ||
          this.state.searchText.length === 0
        ) {
          this.consoleRequest.searchText = this.state.searchText;
          if (this.socket) {
            this.socket.emit("getConsole", this.consoleRequest);
          }

          if (this.state.searchText.length === 0) {
            this.setState({
              lastSearchText: "",
              pendingCounterShortcutSearch: null,
            });
          }
        }
      });
      return;
    }

    this.setState({ [type]: value }, () => {
      if (
        this.state.searchText.length > 1 ||
        this.state.searchText.length === 0
      ) {
        this.consoleRequest.searchText = this.state.searchText;
        if (this.socket) {
          this.socket.emit("getConsole", this.consoleRequest);
        }

        if (this.state.searchText.length === 0) {
          this.setState({
            lastSearchText: "",
            pendingCounterShortcutSearch: null,
          });
        }
      }
    });
  };

  handleRefreshCounterTable = () => {
    this.setState({
      loadingCounterOrders: true,
      loadingMoreCounter: false,
      pendingCounterShortcutSearch: null,
    }, () => {
      this.consoleRequest.searchText = this.state.searchText || "";

      if (this.socket) {
        this.socket.emit("getConsole", this.consoleRequest);
        return;
      }

      this.fetchCounterOrders();
    });
  };

  openCollectDeliveryDialog = async () => {
    this.setState({
      collectDeliveryOpen: true,
      collectDeliveryLocationTypeId: this.props.locationtype_id,
      collectDeliveryError: "",
      collectDeliveryStockCode: "",
      collectDeliveryBadge: "",
    });

    if (this.state.collectDeliveryUsers.length > 0) {
      return;
    }

    try {
      const users = await fetchDeliveryUsers();
      this.setState({ collectDeliveryUsers: users });
    } catch (error) {
      this.setState({
        collectDeliveryError: COLLECT_DELIVERY_MESSAGES.loadUsers,
      });
    }
  };

  closeCollectDeliveryDialog = () => {
    this.setState({
      collectDeliveryOpen: false,
      collectDeliveryError: "",
      collectDeliveryStockCode: "",
      collectDeliveryBadge: "",
      collectDeliveryUserId: "",
      collectDeliveryCart: [],
      collectDeliveryLocationTypeId: "",
      collectDeliveryLoading: false,
      collectDeliveryConfirming: false,
    });
  };

  handleCollectDeliveryUserChange = (event, selectedUser) => {
    this.setState({
      collectDeliveryUserId: selectedUser ? selectedUser.id : "",
      collectDeliveryBadge: selectedUser ? getDeliveryUserLabel(selectedUser) : "",
      collectDeliveryError: "",
    });
  };

  handleCollectDeliveryUserInputChange = (event, inputValue, reason) => {
    const badgeInput = inputValue || "";
    const matchedUser =
      reason === "input" ? findUserByBadgeId(this.state.collectDeliveryUsers, badgeInput) : null;

    this.setState({
      collectDeliveryBadge: badgeInput,
      collectDeliveryUserId: matchedUser
        ? matchedUser.id
        : reason === "input"
          ? ""
          : this.state.collectDeliveryUserId,
      collectDeliveryError: "",
    });
  };

  handleCollectDeliveryBadgeScan = async (badgeValue) => {
    const badgeId = normalizeBadgeId(badgeValue);
    if (!badgeId) {
      this.setState({ collectDeliveryError: COLLECT_DELIVERY_MESSAGES.noUser });
      return;
    }

    const localMatch = findUserByBadgeId(this.state.collectDeliveryUsers, badgeId);
    if (localMatch) {
      this.setState({
        collectDeliveryUserId: localMatch.id,
        collectDeliveryBadge: getDeliveryUserLabel(localMatch),
        collectDeliveryError: "",
      });
      return;
    }

    try {
      const result = await lookupDeliveryUserByBadgeId(badgeId);
      if (!result.success || !result.user) {
        this.setState({
          collectDeliveryError: result.message || COLLECT_DELIVERY_MESSAGES.badgeNotFound,
        });
        return;
      }

      this.setState((prevState) => {
        const alreadyListed = (prevState.collectDeliveryUsers || []).some(
          (deliveryUser) => deliveryUser.id === result.user.id
        );
        return {
          collectDeliveryUsers: alreadyListed
            ? prevState.collectDeliveryUsers
            : [...prevState.collectDeliveryUsers, result.user],
          collectDeliveryUserId: result.user.id,
          collectDeliveryBadge: getDeliveryUserLabel(result.user),
          collectDeliveryError: "",
        };
      });
    } catch (error) {
      this.setState({
        collectDeliveryError: COLLECT_DELIVERY_MESSAGES.badgeNotFound,
      });
    }
  };

  handleCollectDeliveryStockCodeChange = (event) => {
    this.setState({
      collectDeliveryStockCode: event.target.value,
      collectDeliveryError: "",
    });
  };

  addCollectDeliveryOrder = async (orderOrStock) => {
    const fromQueue = orderOrStock && typeof orderOrStock === "object";
    const stockCode = fromQueue ? orderOrStock.stock_code : orderOrStock;

    const locationTypeId =
      this.state.collectDeliveryLocationTypeId || this.props.locationtype_id;

    if (fromQueue && isOrderInCart(this.state.collectDeliveryCart, orderOrStock)) {
      this.setState({ collectDeliveryError: COLLECT_DELIVERY_MESSAGES.alreadyInCart });
      return;
    }
    if (!fromQueue && isOrderInCart(this.state.collectDeliveryCart, { stock_code: stockCode })) {
      this.setState({
        collectDeliveryError: COLLECT_DELIVERY_MESSAGES.alreadyInCart,
        collectDeliveryStockCode: "",
      });
      return;
    }

    this.setState({ collectDeliveryLoading: true, collectDeliveryError: "" });

    try {
      const result = fromQueue
        ? orderOrStock.id
          ? await collectOrderById(orderOrStock.id, locationTypeId)
          : await collectOrderByStock(orderOrStock.stock_code, locationTypeId)
        : await collectOrderByStock(stockCode, locationTypeId);

      if (!result.success || !result.order) {
        this.setState({
          collectDeliveryError: result.message || COLLECT_DELIVERY_MESSAGES.notReady,
          collectDeliveryLoading: false,
        });
        return;
      }

      if (isOrderInCart(this.state.collectDeliveryCart, result.order)) {
        this.setState({
          collectDeliveryError: COLLECT_DELIVERY_MESSAGES.alreadyInCart,
          collectDeliveryStockCode: "",
          collectDeliveryLoading: false,
        });
        return;
      }

      this.setState((prevState) => ({
        collectDeliveryCart: [...prevState.collectDeliveryCart, result.order],
        collectDeliveryStockCode: "",
        collectDeliveryLoading: false,
        collectDeliveryError: "",
      }));
    } catch (error) {
      this.setState({
        collectDeliveryError: COLLECT_DELIVERY_MESSAGES.addStock,
        collectDeliveryLoading: false,
      });
    }
  };

  addCollectDeliveryStockCode = (stockCode) => {
    this.addCollectDeliveryOrder(stockCode || this.state.collectDeliveryStockCode);
  };

  removeCollectDeliveryCartOrder = (orderId) => {
    this.setState((prevState) => ({
      collectDeliveryCart: (prevState.collectDeliveryCart || []).filter(
        (order) => order.id !== orderId
      ),
      collectDeliveryError: "",
    }));
  };

  confirmCollectDelivery = async () => {
    const { collectDeliveryUserId, collectDeliveryCart } = this.state;
    this.setState({ collectDeliveryConfirming: true, collectDeliveryError: "" });

    try {
      const result = await submitCollectDelivery({
        userId: collectDeliveryUserId,
        cart: collectDeliveryCart,
        locationTypeId:
          this.state.collectDeliveryLocationTypeId || this.props.locationtype_id,
      });

      if (!result.success) {
        this.setState({
          collectDeliveryConfirming: false,
          collectDeliveryError: result.message || COLLECT_DELIVERY_MESSAGES.confirmFailed,
        });
        return;
      }

      if (result.failedOrderIds && result.failedOrderIds.length > 0) {
        this.setState({
          collectDeliveryConfirming: false,
          collectDeliveryCart: collectDeliveryCart.filter((order) =>
            result.failedOrderIds.includes(order.id)
          ),
          collectDeliveryError: COLLECT_DELIVERY_MESSAGES.partialFailed,
        });
        this.handleRefreshCounterTable();
        return;
      }

      toast.success(
        this.props.t("CollectDelivery.Success", {
          defaultValue: "Orders moved to Out for Delivery.",
        })
      );
      if (result.emailDetails && result.emailDetails.length > 0) {
        toast.warn(
          this.props.t("CollectDelivery.NotifyFailed", {
            defaultValue: COLLECT_DELIVERY_MESSAGES.notifyFailed,
          })
        );
      }
      this.setState(
        {
          collectDeliveryOpen: false,
          collectDeliveryCart: [],
          collectDeliveryStockCode: "",
          collectDeliveryBadge: "",
          collectDeliveryUserId: "",
          collectDeliveryLocationTypeId: "",
          collectDeliveryConfirming: false,
          collectDeliveryError: "",
        },
        this.handleRefreshCounterTable
      );
    } catch (error) {
      this.setState({
        collectDeliveryConfirming: false,
        collectDeliveryError: COLLECT_DELIVERY_MESSAGES.confirmFailed,
      });
    }
  };


  // Date utility methods moved to DateUtils class
  // Use DateUtils.normalizeDobValue() and DateUtils.formatDobParts()

  openPatientVerificationDialog = (order = null, nameOverride = null, dobOverride = null) => {
    const patientName = order
      ? (order.customerName || `${order.first_name || ""} ${order.last_name || ""}`.trim())
      : (nameOverride !== null ? nameOverride : (this.state.searchText || "")).trim();

    this.setState({
      isPatientVerificationOpen: true,
      patientVerifyName: patientName,
      patientVerifyDob: dobOverride !== null ? dobOverride : "",
      patientVerifyError: "",
      isSubmittingPatientVerification: false,
      pendingVerificationOrder: order, // Store the order to open after verification
    });
  };

  handleSearchEnterStrict = () => {
    const { searchText, counterOrders } = this.state;
    if (!searchText || !searchText.trim()) {
      return;
    }

    // Get the filtered and sorted list (same as table)
    const filteredOrders = counterOrders.filter((order) => {
      if (!order) return false;
      const typeId = order.type_id;
      return (
        typeId !== DEVICE_TYPE.Completed &&
        typeId !== DEVICE_TYPE.Returned &&
        typeId !== DEVICE_TYPE.Cancelled
      );
    }).sort((a, b) =>
      compareOrders(a, b, DEVICE_TYPE, {
        isCurbside: this.props.locationtype_id === LOCATION_TYPE_ID.Curbside,
      })
    );

    const match = SearchUtils.findExactMatch(
      searchText,
      filteredOrders,
      DateUtils.normalizeDobValue
    );

    if (match) {
      if (match.matchType === "dob") {
        const patientName = match.order.customerName ||
          `${match.order.first_name || ""} ${match.order.last_name || ""}`.trim();
        this.openPatientVerificationDialog(match.order, patientName, searchText);
      } else if (match.matchType === "name") {
        this.openPatientVerificationDialog(match.order, searchText, "");
      }
    }
  };

  closePatientVerificationDialog = () => {
    this.setState({
      isPatientVerificationOpen: false,
      patientVerifyError: "",
      isSubmittingPatientVerification: false,
      pendingVerificationOrder: null, // Clear the pending order
    });
  };

  handlePatientVerifyNameChange = (value) => {
    this.setState({ patientVerifyName: value, patientVerifyError: "" });
  };

  handlePatientVerifyDobChange = (value) => {
    this.setState({ patientVerifyDob: value, patientVerifyError: "" });
  };

  submitPatientVerification = () => {
    const {
      patientVerifyName,
      patientVerifyDob,
      isSubmittingPatientVerification,
      counterOrders,
      pendingVerificationOrder,
    } = this.state;
    const { t } = this.props;

    if (isSubmittingPatientVerification) {
      return;
    }

    const trimmedName = (patientVerifyName || "").trim();
    if (!trimmedName) {
      this.setState({
        patientVerifyError: t("Console.Counter.PatientNameError", {
          defaultValue: "Enter the patient's name.",
        }),
      });
      return;
    }

    const normalizedDob = DateUtils.normalizeDobValue(patientVerifyDob);
    if (!normalizedDob) {
      this.setState({
        patientVerifyError: t("Console.Counter.DobFormatError", {
          defaultValue: "Enter a valid date of birth (MM/DD/YYYY).",
        }),
      });
      return;
    }

    const normalizedInputName = trimmedName
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

    if (!Array.isArray(counterOrders) || counterOrders.length === 0) {
      this.setState({
        patientVerifyError: t("Console.Counter.PatientNotFound", {
          defaultValue: "No matching patient found. Check the details and try again.",
        }),
      });
      return;
    }

    // If there's a pending order from row click, use it only when it has DOB and name; otherwise search all orders
    let matchedOrder;
    const pendingDob = pendingVerificationOrder
      ? DateUtils.normalizeDobValue(
          pendingVerificationOrder.dob_str || pendingVerificationOrder.dob || pendingVerificationOrder.dobStr
        )
      : null;
    const pendingNameRaw = pendingVerificationOrder
      ? (pendingVerificationOrder.customerName
          || `${pendingVerificationOrder.first_name || ""} ${pendingVerificationOrder.last_name || ""}`.trim())
      : "";
    const pendingHasData = pendingVerificationOrder && pendingDob && (pendingNameRaw || "").trim().length > 0;

    if (pendingHasData) {
      const storedDob = pendingDob;
      const storedNameRaw = pendingNameRaw;

      if (storedDob !== normalizedDob) {
        this.setState({
          patientVerifyError: t("Console.Counter.PatientNotFound", {
            defaultValue: "No matching patient found. Check the details and try again.",
          }),
        });
        return;
      }

      const storedCustomerName = (storedNameRaw || "")
        .toString()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

      if (!storedCustomerName || normalizedInputName !== storedCustomerName) {
        this.setState({
          patientVerifyError: t("Console.Counter.PatientNotFound", {
            defaultValue: "No matching patient found. Check the details and try again.",
          }),
        });
        return;
      }

      matchedOrder = pendingVerificationOrder;
    } else {
      // Search through all counter orders (button flow or pending order missing DOB/name)
      matchedOrder = counterOrders.find((order) => {
        if (!order) {
          return false;
        }

        const storedDob = DateUtils.normalizeDobValue(
          order.dob_str || order.dob || order.dobStr
        );

        if (!storedDob || storedDob !== normalizedDob) {
          return false;
        }

        const storedNameRaw = order.customerName
          || `${order.first_name || ""} ${order.last_name || ""}`.trim();
        const storedCustomerName = (storedNameRaw || "")
          .toString()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

        if (!storedCustomerName) {
          return false;
        }

        return normalizedInputName === storedCustomerName;
      });

      if (!matchedOrder) {
        this.setState({
          patientVerifyError: t("Console.Counter.PatientNotFound", {
            defaultValue: "No matching patient found. Check the details and try again.",
          }),
        });
        return;
      }
    }

    this.setState(
      {
        isSubmittingPatientVerification: true,
        patientVerifyError: "",
      },
      () => {
        this.closePatientVerificationDialog();
        Promise.resolve(
          writeAuditLog(
            `Patient verification successful for counter order ${matchedOrder.pucode || matchedOrder.pickup_code || matchedOrder.id || "unknown"}.`,
            "Counter Operations"
          )
        )
          .catch((auditError) => {
            console.error("Failed to write audit log for patient verification:", auditError);
          })
          .finally(() => {
            Promise.resolve(
              this.handleOpen(
                matchedOrder.DeliveryType || "counter",
                matchedOrder,
                "verify_patient"
              )
            ).finally(() => {
              this.setState({ isSubmittingPatientVerification: false });
            });
          });
      }
    );
  };

  findAndOpenNextOrder = () => {
    const { patientOrders, id: currentOrderId, customer_id } = this.state;

    if (!patientOrders || patientOrders.length <= 1) {
      this.setState({ open: false, patientOrders: [] });
      return;
    }

    // Get patient orders from counterOrders, but merge with current patientOrders state
    // to preserve any local status updates that haven't been synced via socket yet
    const refreshedFromCounter = this.state.counterOrders.filter(
      (order) => order && order.customer_id === customer_id
    );

    // Merge: Use current patientOrders status if order exists there, otherwise use counterOrders
    const refreshedPatientOrders = refreshedFromCounter.map((counterOrder) => {
      const localOrder = (patientOrders || []).find(
        (po) => po && po.id === counterOrder.id
      );
      // Prefer local order status if it exists (has latest updates)
      return localOrder || counterOrder;
    });

    // Also add any orders in patientOrders that might not be in counterOrders yet
    (patientOrders || []).forEach((localOrder) => {
      if (
        localOrder &&
        String(localOrder.customer_id) === String(customer_id) &&
        !refreshedPatientOrders.find(ro => ro && ro.id === localOrder.id)
      ) {
        refreshedPatientOrders.push(localOrder);
      }
    });

    // Find current order's position BEFORE filtering (so we know where it was in sequence)
    const currentOrderIndexInFullList = refreshedPatientOrders.findIndex(
      (order) => order && order.id === currentOrderId
    );

    const unprocessedOrders = refreshedPatientOrders.filter((order) => {
      if (!order) return false;
      const typeId = order.type_id;
      const isProcessed = (
        typeId === DEVICE_TYPE.Completed ||
        typeId === DEVICE_TYPE.Returned ||
        typeId === DEVICE_TYPE.Cancelled
      );
      return !isProcessed;
    });

    if (unprocessedOrders.length === 0) {
      this.setState({ open: false, patientOrders: [] });
      return;
    }

    let nextOrder = null;

    // If we found the current order's position in the full list
    if (currentOrderIndexInFullList >= 0) {
      // Find the next unprocessed order AFTER the current order's position
      for (let i = currentOrderIndexInFullList + 1; i < refreshedPatientOrders.length; i++) {
        const order = refreshedPatientOrders[i];
        if (order) {
          const typeId = order.type_id;
          const isProcessed = (
            typeId === DEVICE_TYPE.Completed ||
            typeId === DEVICE_TYPE.Returned ||
            typeId === DEVICE_TYPE.Cancelled
          );
          if (!isProcessed) {
            nextOrder = order;
            break;
          }
        }
      }

      // If no next order found after current, wrap to beginning
      if (!nextOrder) {
        for (let i = 0; i < currentOrderIndexInFullList; i++) {
          const order = refreshedPatientOrders[i];
          if (order) {
            const typeId = order.type_id;
            const isProcessed = (
              typeId === DEVICE_TYPE.Completed ||
              typeId === DEVICE_TYPE.Returned ||
              typeId === DEVICE_TYPE.Cancelled
            );
            if (!isProcessed) {
              nextOrder = order;
              break;
            }
          }
        }
      }
    }

    // Fallback: if current order not found or no next order found, use first unprocessed order
    if (!nextOrder && unprocessedOrders.length > 0) {
      nextOrder = unprocessedOrders[0];
    }

    if (nextOrder) {
      this.setState({ patientOrders: refreshedPatientOrders }, () => {
        this.handleOpen(
          nextOrder.DeliveryType || "counter",
          nextOrder,
          "auto_advance"
        );
      });
    } else {
      this.setState({ open: false, patientOrders: [] });
    }
  };

  moveHandler = async (type) => {
    const { patientOrders, id: currentOrderId } = this.state;

    let newTypeId = null;
    if (type === "complete") {
      newTypeId = DEVICE_TYPE.Completed;
    } else if (type === "return") {
      newTypeId = DEVICE_TYPE.Returned;
    } else if (type === "cancel") {
      newTypeId = DEVICE_TYPE.Cancelled;
    } else if (type === "hold") {
      newTypeId = DEVICE_TYPE.Hold;
    } else if (type === "release") {
      newTypeId = DEVICE_TYPE.Ready;
    }

    const success = await this.orderService.moveHandler(type);
    if (success && newTypeId) {
      // Update patientOrders with new status - socket will sync and filter will remove processed orders
      const updatedPatientOrders = (patientOrders || []).map((order) => {
        if (order && order.id === currentOrderId) {
          return { ...order, type_id: newTypeId };
        }
        return order;
      });

      this.setState({ patientOrders: updatedPatientOrders }, () => {
        // Check if there are still unprocessed orders
        const unprocessedOrders = updatedPatientOrders.filter((order) => {
          if (!order) return false;
          const typeId = order.type_id;
          return (
            typeId !== DEVICE_TYPE.Completed &&
            typeId !== DEVICE_TYPE.Returned &&
            typeId !== DEVICE_TYPE.Cancelled
          );
        });

        if (unprocessedOrders.length <= 1) {
          // Close dialog if only one or no orders left
          this.handleClose();
        }
      });
    }
  };

  // Batch complete all orders
  handleBatchComplete = async (isDispenseCall = false) => {
    const { patientOrders, orderFullDetailsMap, orderCompletionStatus } = this.state;

    const unprocessedOrders = (patientOrders || []).filter((order) => {
      if (!order) return false;
      const typeId = order.type_id;
      return (
        typeId !== DEVICE_TYPE.Completed &&
        typeId !== DEVICE_TYPE.Returned &&
        typeId !== DEVICE_TYPE.Cancelled
      );
    });

    if (unprocessedOrders.length === 0) {
      return;
    }

    // Filter to only fully complete orders
    const fullyCompleteOrders = unprocessedOrders.filter((order) => {
      const completionStatus = orderCompletionStatus[order.id];
      return completionStatus && completionStatus.isFullyComplete === true;
    });

    if (fullyCompleteOrders.length === 0) {
      return;
    }

    // Helper function to check if dlrawdata exists and is valid
    const hasValidDlrawdata = (idData) => {
      if (!idData || !idData.dlrawdata) return false;
      const dlrawdata = idData.dlrawdata;
      return typeof dlrawdata === "string" &&
        dlrawdata.trim() !== "" &&
        dlrawdata !== "null" &&
        dlrawdata !== "undefined";
    };

    // Check if any fully complete order has ID scan data, and copy it to orders that don't have it
    let idScanData = null;

    // Find first fully complete order with ID scan data
    for (const order of fullyCompleteOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      if (fullDetails && fullDetails.idData && hasValidDlrawdata(fullDetails.idData)) {
        idScanData = fullDetails.idData.dlrawdata;
        break;
      }
    }

    // If we found ID scan data, copy it to fully complete orders that don't have it
    if (idScanData) {
      const ordersWithoutIdData = fullyCompleteOrders.filter((order) => {
        const fullDetails = orderFullDetailsMap[order.id] || order;
        return !(fullDetails && fullDetails.idData && hasValidDlrawdata(fullDetails.idData));
      });

      if (ordersWithoutIdData.length > 0) {
        const orderIdsToUpdate = ordersWithoutIdData.map((o) => o.id);
        try {
          await this.orderService.handleBatchScanId(orderIdsToUpdate, idScanData);
        } catch (error) {
          console.error("Error copying ID scan data:", error);
          // Continue with completion even if ID copy fails
        }
      }
    }

    // Check if any fully complete order has signature, and copy it to orders that don't have it
    let signatureData = null;

    // Find first fully complete order with signature
    for (const order of fullyCompleteOrders) {
      const fullDetails = orderFullDetailsMap[order.id] || order;
      const sigUrl = (fullDetails && fullDetails.signature_url) || (order && order.signature_url);
      if (sigUrl && typeof sigUrl === "string" && sigUrl.trim() !== "" && sigUrl !== "null" && sigUrl !== "undefined") {
        signatureData = sigUrl;
        break;
      }
    }

    // If we found signature, copy it to fully complete orders that don't have it
    if (signatureData) {
      const ordersWithoutSignature = fullyCompleteOrders.filter((order) => {
        const fullDetails = orderFullDetailsMap[order.id] || order;
        const sigUrl = (fullDetails && fullDetails.signature_url) || (order && order.signature_url);
        return !(sigUrl && typeof sigUrl === "string" && sigUrl.trim() !== "" && sigUrl !== "null" && sigUrl !== "undefined");
      });

      if (ordersWithoutSignature.length > 0) {
        const orderIdsToUpdate = ordersWithoutSignature.map((o) => o.id);
        try {
          await this.orderService.handleBatchSubmitSignature(signatureData, orderIdsToUpdate);
        } catch (error) {
          console.error("Error copying signature data:", error);
          // Continue with completion even if signature copy fails
        }
      }
    }

    // Use moveHandler with array of order IDs (uses batch API internally)
    const orderIds = fullyCompleteOrders
      .filter((order) => order && order.id)
      .map((order) => order.id);

    if (orderIds.length === 0) {
      return;
    }

    try {
      // Get detailed batch result for better error handling
      const batchResult = await this.orderService.batchMoveHandler(orderIds, "complete", { isDispenseCall });

      const processed = batchResult.processed || 0;
      const failed = batchResult.failed || 0;
      const successfulOrderIds = batchResult.successful_order_ids || [];
      const failedOrderIds = batchResult.failed_order_ids || [];

      if (batchResult.success) {
        // All orders completed successfully
        console.log(`Successfully completed ${processed} order(s)`);
        if (batchResult.emails_sent > 0) {
          console.log(`${batchResult.emails_sent} email(s) sent`);
        }

        // Show success toast message
        toast.success(
          this.props.t("Console.SuccessMessage.ReceiptEmailed", {
            defaultValue: "Success, receipt has been emailed",
          })
        );
      } else {
        // Some orders failed
        if (failed > 0) {
          console.warn(`${failed} order(s) failed to complete:`, failedOrderIds);
        }
      }

      // Update patientOrders state with new status for successful orders only
      if (successfulOrderIds.length > 0) {
        const updatedPatientOrders = (this.state.patientOrders || []).map((order) => {
          if (order && order.id && successfulOrderIds.includes(order.id)) {
            return { ...order, type_id: DEVICE_TYPE.Completed };
          }
          return order;
        });
        this.setState({ patientOrders: updatedPatientOrders });
      }
    } catch (error) {
      console.error("Error in batch complete:", error);
    }

    // Close dialog after all orders are completed
    setTimeout(() => {
      this.handleClose();
    }, 500);
  };

  // Handle individual order cancel
  handleCancelOrder = (orderId) => {
    this.setState({ id: orderId }, () => {
      this.cancelHandlerOrder();
    });
  };

  // Handle individual order return
  handleReturnOrder = (orderId) => {
    this.setState({ id: orderId }, () => {
      this.moveHandler("return");
    });
  };

  handleHoldOrder = (orderId) => {
    this.setState({ id: orderId }, () => {
      this.moveHandler("hold");
    });
  };

  handleReleaseOrder = (orderId) => {
    this.setState({ id: orderId }, () => {
      this.moveHandler("release");
    });
  };

  reminderHandler = async (orderId) => {
    const result = await this.orderService.reminderHandler(orderId);
    if (result && result.ok) {
      toast.success(
        this.props.t("Prescriptions.Messages.NotificationSent", {
          defaultValue: "Notification sent",
        })
      );
      return;
    }
    toast.error(
      this.props.t("Prescriptions.Messages.NotificationNotSent", {
        defaultValue: "Notification not sent",
      })
    );
  };

  handleReprintOrder = async (order) => {
    if (!order || !order.id || !this.props.onOpenPrintDialogWithRxByBin) {
      return;
    }

    const batchResult = await this.orderService.getOrderDetailsBatch([order.id]);
    const freshOrder =
      batchResult.orders && batchResult.orders.length > 0
        ? batchResult.orders[0]
        : null;
    const fullDetails =
      freshOrder || this.state.orderFullDetailsMap[order.id] || order;

    if (freshOrder) {
      this.setState((prevState) => ({
        orderFullDetailsMap: {
          ...prevState.orderFullDetailsMap,
          [order.id]: freshOrder,
        },
      }));
    }

    const boxId = fullDetails.box_id || order.box_id;
    const binId = fullDetails.bin_id || order.bin_id || null;
    const stockCode = fullDetails.stock_code || order.stock_code || null;

    await this.props.onOpenPrintDialogWithRxByBin(boxId, binId, order.id, {
      fromReprint: true,
      stockCode,
    });
  };

  printTemplete = async () => {
    const { popupData } = this.state;
    this.setState({ open: false, printDialogOpen: true });

    const pickupCode =
      (popupData && (popupData.pucode || popupData.pickup_code)) || "";
    try {
      await writeAuditLog(
        `Print initiated for counter order ${pickupCode || (popupData && popupData.id) || "unknown"}.`,
        "Counter Operations"
      );
    } catch (auditError) {
      console.error("Failed to write audit log for print action:", auditError);
    }

    setTimeout(() => {
      window.print();
      this.setState({ printDialogOpen: false });
    }, 500);
  };

  cancelHandler = () => {
    this.setState({ deletepopup: true });
  };

  handleCloseCancel = () => {
    this.setState({ deletepopup: false });
  };

  cancelHandlerOrder = () => {
    this.setState({ deletepopup: false });
    this.moveHandler("cancel");
  };

  handleRemoveRxItem = (index) => {
    // If parent provides onRemoveItem handler, use it
    if (this.props.onRemoveItem) {
      this.props.onRemoveItem(index);
    } else {
      // Otherwise, handle it locally if we have access to update searchedRxItems
      // This is a fallback - ideally the parent should provide the handler
      console.warn("onRemoveItem handler not provided by parent component");
    }
  };

  orderMatchesCustomerOrPatientId = (order, customerId) => {
    if (!order || order.type_id !== DEVICE_TYPE.Ready) return false;
    const idStr = customerId != null ? String(customerId).trim() : "";
    if (!idStr) return false;
    if (order.customer_id != null && String(order.customer_id) === idStr) return true;
    const patientIdFields = [
      order.patient_record_number,
      order.PatientID,
      order.patient_id,
      order.medical_record_number,
      order.MedicalRecordNumber,
      order.patientRecordNumber,
    ];
    return patientIdFields.some((f) => f != null && String(f).trim() === idStr);
  };

  isOrderPaidForAddTo = (order) => {
    if (!order) return false;
    if (order.amount && order.amount.isPaid === true) return true;
    if (order.is_paid === true) return true;
    return false;
  };

  getReadyOrdersForCurrentPatient = () => {
    const { counterOrders } = this.state;
    const { customerId } = this.props;

    if (!counterOrders || counterOrders.length === 0) return [];
    if (customerId == null || customerId === "") return [];

    return counterOrders.filter(
      (order) =>
        this.orderMatchesCustomerOrPatientId(order, customerId) &&
        !this.isOrderPaidForAddTo(order)
    );
  };

  identifiesAddToByStockCode = () => {
    return this.props.locationtype_id !== LOCATION_TYPE_ID.Counter;
  };

  handleOpenAddToDialog = () => {
    const readyOrders = this.getReadyOrdersForCurrentPatient();
    const identifyByStockCode = this.identifiesAddToByStockCode();
    const firstId =
      !identifyByStockCode && readyOrders.length > 0 && readyOrders[0].id
        ? readyOrders[0].id
        : null;
    this.setState({
      addToDialogOpen: true,
      addToSelectedOrderId: firstId,
    });
  };

  handleCloseAddToDialog = () => {
    this.setState({
      addToDialogOpen: false,
      addToSelectedOrderId: null,
    });
  };

  handleAddToSelectOrder = (orderId) => {
    this.setState({ addToSelectedOrderId: orderId ? Number(orderId) || orderId : null });
  };

  /**
   * Build items array for /prescription/addItems from cart (searchedRxItems + Non-Rx).
   * API expects: rx_no, drug_name, ndc_code, price, qty, fillNumber, prescriptionnumber,
   * order_id, prescription_txn_id, is_consultrequire, is_returnable, tax_amount.
   * Non-Rx lines use is_non_rx, item_number, drug_name, price, qty.
   */
  buildAddItemsPayloadFromCart = (searchedRxItems, accountDetails, nonRxCartItems = []) => {
    const connectionId = accountDetails && accountDetails.connection && accountDetails.connection.id;
    const rxItems = Array.isArray(searchedRxItems) ? searchedRxItems : [];
    const payload = rxItems.map((item) => {
      let rxNo = item.rxNo;
      if (connectionId === CONNECTION_TYPE.QS1 && rxNo) {
        const first = String(rxNo).substring(0, 1);
        if ((first === "0" || first === 0) && String(rxNo).length > 8) {
          rxNo = String(rxNo).substring(1, String(rxNo).length);
        }
      } else if (connectionId === CONNECTION_TYPE.THIRD_PARTY && item.id) {
        rxNo = item.id;
      }
      const priceVal = item.CoPayAmount != null ? item.CoPayAmount : item.amount_due;
      const price = [undefined, "undefined", null, "null", ""].includes(priceVal)
        ? 0
        : Number(priceVal);
      const fillNumVal = item.fillNumber != null ? item.fillNumber : item.fillnumber;
      const fillNumber = [undefined, "undefined", null, "null", ""].includes(fillNumVal)
        ? 0
        : Number(fillNumVal);
      const taxAmount = [undefined, "undefined", null, "null", "", "0", 0].includes(
        item.tax_amount
      )
        ? 0
        : typeof item.tax_amount === "string"
        ? parseFloat(item.tax_amount)
        : item.tax_amount;
      const qtyVal = item.qty != null ? item.qty : (item.dispensed_qty != null ? item.dispensed_qty : 0);
      const consultVal = item.ConsultRequire != null ? item.ConsultRequire : item.is_consultrequire;
      return {
        rx_no: rxNo != null ? rxNo : "",
        drug_name: item.name || item.drug_name || "",
        ndc_code: item.ndcCode || "",
        price,
        qty: qtyVal,
        fillNumber,
        prescriptionnumber: item.prescriptionNumber != null ? item.prescriptionNumber : "",
        order_id: item.order_id != null ? item.order_id : "",
        prescription_txn_id: item.prescription_txn_id != null ? item.prescription_txn_id : "",
        is_consultrequire: Boolean(consultVal),
        is_returnable: Boolean(
          ![undefined, "undefined", null, "null", ""].includes(item.IsReturnable) && item.IsReturnable
        ),
        tax_amount: taxAmount,
      };
    });

    if (this.props.isNonRxDeliverEnabled && Array.isArray(nonRxCartItems)) {
      nonRxCartItems.forEach((nonRxItem) => {
        payload.push({
          is_non_rx: true,
          item_number: (nonRxItem.item_number || "").toString().trim(),
          drug_name: (nonRxItem.description || "").toString().trim(),
          price: [undefined, "undefined", null, "null", ""].includes(nonRxItem.price)
            ? 0
            : parseFloat(nonRxItem.price),
          qty: parseFloat(nonRxItem.qty) || 1,
          tax_amount: parseFloat(nonRxItem.tax_amount) || 0,
          upc: (nonRxItem.upc || "").toString().trim() || null,
          uom: (nonRxItem.uom || "").toString().trim() || null,
          non_rx_product_id: nonRxItem.non_rx_product_id || null,
          image_url: (nonRxItem.image_url || "").toString().trim() || null,
        });
      });
    }
    return payload;
  };

  callAddToOrderApi = async (orderId, openPrintDialog) => {
    const { searchedRxItems = [], accountDetails, nonRxCartItems = [] } = this.props;
    this.setState({ addToSubmitting: true });
    try {
      const items = this.buildAddItemsPayloadFromCart(
        searchedRxItems,
        accountDetails,
        nonRxCartItems
      );
      if (!items.length) {
        throw new Error(
          this.props.t("Console.AddToOrder.NoItems", {
            defaultValue: "No prescription items to add.",
          })
        );
      }
      const payload = {
        prescription_id: Number(orderId) || orderId,
        items,
      };
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "prescription/addItems",
        {
          method: "POST",
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = response.ok ? null : await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          (data && data.message) ||
          this.props.t("Console.AddToOrder.Error", {
            defaultValue: "Failed to add to order. Please try again.",
          });
        throw new Error(message);
      }

      const selectedOrder = this.getReadyOrdersForCurrentPatient().find(
        (o) => o && String(o.id) === String(orderId)
      );
      const accountId = (selectedOrder && selectedOrder.parent_account_id) || (accountDetails && accountDetails.id);
      const locationTypeId = (selectedOrder && selectedOrder.locationtype_id) || LOCATION_TYPE_ID.Counter;
      const courierTracking = this.props.courier_tracking != null ? this.props.courier_tracking : "";
      const defaultCourierSelected = this.props.default_courier_selected != null ? this.props.default_courier_selected : "";
      const notificationPayload = {
        order_id: Number(orderId) || orderId,
        account_id: accountId,
        location_type_id: locationTypeId,
        courier_tracking: courierTracking,
        default_courier_selected: defaultCourierSelected,
      };
      const notificationEndpoint = locationTypeId === LOCATION_TYPE_ID.Bedside
        ? "delivery/bedsidenotification"
        : "delivery/notifications";
      fetch(process.env.REACT_APP_BASEURL + notificationEndpoint, {
        method: "POST",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(notificationPayload),
      }).catch(() => {});

      if (this.socket) this.socket.emit("updateConsole");

      toast.success(
        this.props.t("Console.AddToOrder.Success", {
          defaultValue: "Prescription(s) added to order.",
        })
      );

      this.setState({ addToDialogOpen: false, addToSelectedOrderId: null });

      if (!openPrintDialog && this.props.onClear) this.props.onClear();

      if (openPrintDialog && this.props.onOpenPrintDialogWithRxByBin) {
        const batchResult = await this.orderService.getOrderDetailsBatch([orderId]);
        if (batchResult.orders && batchResult.orders.length > 0) {
          const orderData = batchResult.orders[0];
          this.setState((prevState) => ({
            orderFullDetailsMap: {
              ...prevState.orderFullDetailsMap,
              [orderId]: orderData,
            },
          }));
          const boxId = orderData.box_id != null ? orderData.box_id : null;
          const binId = orderData.bin_id != null ? orderData.bin_id : (orderData.storage_location != null ? orderData.storage_location : null);
          const pmId = orderData.id != null ? orderData.id : (orderData.prescription_id != null ? orderData.prescription_id : orderId);
          await this.props.onOpenPrintDialogWithRxByBin(boxId, binId, pmId, { fromAddAndSave: true });
        }
      }
    } catch (err) {
      console.error("Add to order error:", err);
      toast.error(err.message || this.props.t("Console.AddToOrder.Error", { defaultValue: "Failed to add to order. Please try again." }));
      throw err;
    } finally {
      this.setState({ addToSubmitting: false });
    }
  };

  updateOrderDueAmount = async (orderid, dueAmount, orderIdToRefresh) => {
    await this.orderService.updateOrderDueAmount(orderid, dueAmount);
    if (orderIdToRefresh) {
      await this.orderService.refreshOrderDetailsBatch([orderIdToRefresh]);
    }
  };

  // Refresh popup data moved to OrderService
  refreshPopupData = async () => {
    await this.orderService.refreshPopupData();
    // Completion status is now updated in OrderService.refreshPopupData()
  };

  // ID capture handler moved to OrderService - supports single and batch
  handleSubmitIdCapture = async (frontImage, backImage, orderIds = null) => {
    await this.orderService.handleSubmitIdCapture(frontImage, backImage, orderIds);
  };

  // Signature capture handler moved to OrderService - supports single and batch
  handleSubmitSignature = async (signatureData, orderIds = null) => {
    await this.orderService.handleSubmitSignature(signatureData, orderIds);
  };

  // Payment status toggle - supports single order or batch (array of order IDs)
  handleTogglePaymentStatus = async (orderIds = null) => {
    // If orderIds is provided (array), it's a batch operation from MultiPickupDialog
    if (Array.isArray(orderIds) && orderIds.length > 0) {
      await this.orderService.handleBatchTogglePaymentStatus(orderIds);
    } else {
      // Single order operation
      await this.orderService.handleTogglePaymentStatus();
    }
  };

  // Pay Later (payroll deduct) handler
  handlePayrollDeduct = async (orderIds = null) => {
    const ordersToProcess = Array.isArray(orderIds) && orderIds.length > 0
      ? orderIds
      : null;

    if (!ordersToProcess) {
      return;
    }

    this.setState({ isProcessingPayLater: true, paymentStatusError: "" });

    try {
      await this.orderService.handlePayrollDeduct(ordersToProcess, {
        onSuccess: (message) => {
          toast.success(message);
        },
      });
    } catch (error) {
      console.error("Error processing Pay Later:", error);
      const errorMessage = (error && error.message) || "Failed to update Pay Later status. Please try again.";
      toast.error(errorMessage);
      this.setState({ paymentStatusError: errorMessage });
    } finally {
      this.setState({ isProcessingPayLater: false });
    }
  };

  // Cash payment handler - supports batch (array of order IDs)
  handleCashPayment = async (orderIds = null, cashReceived = 0) => {
    const ordersToProcess = Array.isArray(orderIds) && orderIds.length > 0 ? orderIds : null;

    if (!ordersToProcess) {
      toast.error(
        this.props.t
          ? this.props.t("Console.ActionPopup.NoOrdersForPayment", { defaultValue: "No orders selected for payment." })
          : "No orders selected for payment."
      );
      return false;
    }

    this.setState({ isProcessingCashPayment: true, paymentStatusError: "" });

    try {
      const result = await this.orderService.handleCashPayment(ordersToProcess, cashReceived);
      const cashApplied = Number(result && result.cash_applied ? result.cash_applied : 0);
      const balanceOwed = Number(result && result.balance_owed ? result.balance_owed : 0);
      const changeDue = Number(result && result.change_due ? result.change_due : 0);
      const details = [];
      if (cashApplied > 0) details.push("Applied: $" + cashApplied.toFixed(2));
      if (balanceOwed > 0) details.push("Balance: $" + balanceOwed.toFixed(2));
      if (changeDue > 0) details.push("Change: $" + changeDue.toFixed(2));
      toast.success(details.length ? "Cash payment recorded. " + details.join(" | ") : "Cash payment recorded.");
      if (this.state.open && this.state.id) {
        await this.orderService.refreshPopupData();
      }
      return true;
    } catch (error) {
      console.error("Error processing cash payment:", error);
      const errorMessage = (error && error.message) || "Failed to record cash payment. Please try again.";
      toast.error(errorMessage);
      this.setState({ paymentStatusError: errorMessage });
      return false;
    } finally {
      this.setState({ isProcessingCashPayment: false });
    }
  };

  // Terminal payment handler - supports batch (array of order IDs)
  handleTerminalPayment = async (orderIds = null) => {
    // If no orderIds provided, use current order from state
    let ordersToProcess = orderIds;
    if (!Array.isArray(ordersToProcess) || ordersToProcess.length === 0) {
      const currentOrderId = this.state.id;
      if (currentOrderId) {
        ordersToProcess = [currentOrderId];
      } else {
        const { patientOrders } = this.state;
        if (patientOrders && patientOrders.length > 0) {
          ordersToProcess = patientOrders.map((o) => o && o.id).filter(Boolean);
        }
      }
    }

    if (!Array.isArray(ordersToProcess) || ordersToProcess.length === 0) {
      toast.error(
        this.props.t
          ? this.props.t("Console.ActionPopup.NoOrdersForPayment", {
            defaultValue: "No orders selected for payment.",
          })
          : "No orders selected for payment."
      );
      return;
    }

    this.setState({
      isProcessingTerminalPayment: true,
      paymentStatusError: "",
    });

    try {
      const result = await this.orderService.handleTerminalPayment(ordersToProcess);

      if (result && result.status === "cancelled") {
        toast.info(
          this.props.t
            ? this.props.t("Console.ActionPopup.PaymentCancelled", {
              defaultValue: "Payment was cancelled.",
            })
            : "Payment was cancelled."
        );
      } else {
        toast.success(
          this.props.t
            ? this.props.t("Console.ActionPopup.PaymentProcessedSuccessfully", {
              defaultValue: "Payment processed successfully!",
            })
            : "Payment processed successfully!"
        );
      }

      if (this.state.open && this.state.id && (!result || result.status !== "cancelled")) {
        await this.orderService.refreshPopupData();
      }

      this.setState({
        isProcessingTerminalPayment: false,
      });
    } catch (error) {
      console.error("Error processing terminal payment:", error);
      const errorMessage =
        (error && error.message) ||
        (this.props.t
          ? this.props.t("Console.ActionPopup.PaymentProcessingError", {
            defaultValue: "Failed to process payment. Please try again.",
          })
          : "Failed to process payment. Please try again.");

      toast.error(errorMessage);
      this.setState({
        isProcessingTerminalPayment: false,
        paymentStatusError: errorMessage,
      });
    }
  };

  // Start video consultation handler
  handleStartConsult = async (orderIds = null) => {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      // If no orderIds provided, use scanned order IDs
      const scannedOrderIds = Object.keys(this.state.orderBagScanStatus)
        .filter((orderId) => {
          const scanStatus = this.state.orderBagScanStatus[orderId];
          return scanStatus && scanStatus.isScanned === true && scanStatus.isVerified === true;
        })
        .map((id) => Number(id) || id);

      if (scannedOrderIds.length === 0) {
        this.setState({
          videoConsultationError: this.props.t("Console.Consultation.NoScannedOrders", {
            defaultValue: "Please scan at least one order before starting consultation",
          }),
        });
        return;
      }
      orderIds = scannedOrderIds;
    }

    // Open dialog immediately with skeleton loader - don't wait for consultation to start
    this.setState({
      videoConsultationError: "",
      isConsult: true,
      isVideoConsultation: true, // Show video consultation dialog immediately
      isStartingConsultation: true,
      startRinging: false, // Will be set to true when consultation actually starts
      isPharmacistConnected: false,
      isConsultAcceptedCall: false,
    });

    // Start consultation in background (non-blocking)
    try {
      const success = await this.consultationService.startVideoConsultation(orderIds);

      if (!success) {
        this.setState({
          isConsult: false,
          isVideoConsultation: false,
          isStartingConsultation: false,
          videoConsultationError: this.state.videoConsultationError || "Failed to start consultation",
        });
      } else {
        // Clear loading state once consultation starts successfully
        // The consultation service will update the state with video tracks and connection status
        this.setState({
          isStartingConsultation: false,
        });
      }
    } catch (error) {
      this.setState({
        isConsult: false,
        isVideoConsultation: false,
        isStartingConsultation: false,
        videoConsultationError: error.message || "Failed to start consultation",
      });
    }
  };

  // End video consultation handler
  handleEndConsultation = async (isConsultEndCall = false, isUserEndCall = true) => {
    try {
      await this.consultationService.endVideoConsultation(isConsultEndCall, isUserEndCall);
    } catch (error) {
      console.error("Error ending consultation:", error);
    }
  };

  // Close consultation dialog
  handleCloseConsultation = () => {
    if (this.state.isConsult) {
      this.handleEndConsultation(false, true);
    }
    this.setState({
      isConsult: false,
      isConsultNotAvailable: false,
      isReleasedModal: false,
    });
  };

  handleScanId = async (scannedBarcodeValue, orderIds = null) => {
    this.setState({
      isScanningId: true,
      scanIdError: ""
    });

    try {
      await this.orderService.handleScanId(scannedBarcodeValue, orderIds);
      this.setState({
        isScanningId: false,
        scanIdError: ""
      });
    } catch (error) {
      this.setState({
        isScanningId: false,
        scanIdError: error.message || "Failed to save scanned ID. Please try again.",
      });
      throw error;
    }
  };

  handleSaveConsultation = async (notes, orderIds = null) => {
    const { isSavingConsultation } = this.state;

    if (isSavingConsultation) {
      return;
    }

    this.setState({ isSavingConsultation: true, consultationError: "" });

    try {
      // If orderIds is provided (array), it's a batch operation from MultiPickupDialog
      if (Array.isArray(orderIds) && orderIds.length > 0) {
        await this.orderService.saveBatchConsultation(notes || "", orderIds);
      } else {
        // Single order operation
        await this.orderService.saveConsultation(notes || "");
      }
      // Update consultation notes in state
      this.setState({
        isSavingConsultation: false,
        consultationError: "",
        consultationNotes: notes || "", // Update notes in state
      });
    } catch (error) {
      this.setState({
        isSavingConsultation: false,
        consultationError: error.message || "Failed to save consultation. Please try again.",
      });
      throw error; // Re-throw to let dialog handle it
    }
  };

  handleDeclineConsultation = async (orderIds = null) => {
    const { isDecliningConsultation } = this.state;

    if (isDecliningConsultation) {
      return;
    }

    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error("No orders provided for consultation decline.");
    }

    this.setState({ isDecliningConsultation: true, consultationError: "" });

    try {
      await this.orderService.declineBatchConsultation(orderIds);
      this.setState({
        isDecliningConsultation: false,
        consultationError: "",
      });
    } catch (error) {
      this.setState({
        isDecliningConsultation: false,
        consultationError: error.message || "Failed to decline consultation. Please try again.",
      });
      throw error;
    }
  };

  componentDidUpdate(prevProps) {
    // Update strict_patient_verification, enableTrim, trimChars when currentBoxData prop changes
    if (prevProps.currentBoxData !== this.props.currentBoxData) {
      const currentBoxData = this.props.currentBoxData;
      if (currentBoxData) {
        this.setState({
          strictPatientVerification: typeof currentBoxData.strict_patient_verification === "boolean"
            ? currentBoxData.strict_patient_verification
            : false,
          enableTrim: typeof currentBoxData.enable_trim === "boolean"
            ? currentBoxData.enable_trim
            : false,
          trimChars: typeof currentBoxData.trim_chars === "string"
            ? currentBoxData.trim_chars
            : SearchUtils.defaultTrimChars,
        });
      } else {
        this.setState({
          strictPatientVerification: false,
          enableTrim: false,
          trimChars: SearchUtils.defaultTrimChars,
        });
      }
    }

    // Handle location change - refetch data when location changes
    const prevLocationTypeId = prevProps.locationtype_id;
    const currentLocationTypeId = this.props.locationtype_id;
    const prevLocationId = prevProps.currentBoxData && prevProps.currentBoxData.id;
    const currentLocationId = this.props.currentBoxData && this.props.currentBoxData.id;

    if (prevLocationId !== currentLocationId || prevLocationTypeId !== currentLocationTypeId) {
      if (this.state.collectDeliveryOpen && prevLocationTypeId !== currentLocationTypeId) {
        this.closeCollectDeliveryDialog();
      }
      this.consoleRequest.location_id = currentLocationId || null;
      const activeConsoleKey = this.getActiveConsoleKey();
      this.consoleRequest.pageNo[activeConsoleKey] = 1;
      this.consoleRequest.pageSize[activeConsoleKey] = 50;
      if (this.socket) {
        this.socket.emit("getConsole", this.consoleRequest);
        this.setState({ loadingCounterOrders: true });
      } else {
        this.fetchCounterOrders();
      }
    }

    if (
      prevProps.defaultValidatedLocation !== this.props.defaultValidatedLocation ||
      prevProps.binId !== this.props.binId
    ) {
      this.syncDefaultValidatedLocation();
    }

    if (prevProps.binId && !this.props.binId) {
      this.setState({
        validatedLocation: null,
        locationError: "",
        locationTouched: false,
        isValidatingLocation: false,
      });
    }

    const rxNumberCleared = prevProps.rxNumber && !this.props.rxNumber;
    if (
      rxNumberCleared &&
      prevProps.rxNumber &&
      prevProps.rxNumber.length > 0
    ) {
      setTimeout(() => {
        if (this.socket) {
          this.socket.emit("getConsole", this.consoleRequest);
        } else {
          this.fetchCounterOrders();
        }
      }, 1500);
    }

    if (rxNumberCleared) {
      if (
        this.props.searchedRxItems &&
        this.props.searchedRxItems.length === 0 &&
        this.scanRxInputRef.current
      ) {
        setTimeout(() => {
          if (this.scanRxInputRef.current) {
            this.scanRxInputRef.current.focus();
          }
        }, 100);
      } else if (
        this.props.searchedRxItems &&
        this.props.searchedRxItems.length > 0 &&
        this.scanRxInputRef.current
      ) {
        setTimeout(() => {
          if (this.scanRxInputRef.current) {
            this.scanRxInputRef.current.focus();
          }
        }, 100);
      }
    }
  }

  validateLocationAPI = async (shouldAutoFocus = false) => {
    const { binId = "" } = this.props;
    const boxId = this.props.currentBoxData && this.props.currentBoxData.id;

    if (!binId || !binId.trim()) {
      this.setState({
        validatedLocation: null,
        isValidatingLocation: false,
        locationError: "",
      });
      if (this.props.onLocationChange) {
        this.props.onLocationChange(null);
      }
      return;
    }

    if (!boxId) {
      this.setState({
        validatedLocation: null,
        isValidatingLocation: false,
      });
      return;
    }

    const trimmedCode = binId.trim();
    this.setState({ isValidatingLocation: true });

    try {
      const token = JSON.parse(sessionStorage.getItem("user"))
        .signInUserSession.idToken.jwtToken;

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            location_value: trimmedCode,
            box_id: boxId,
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.is_valid && result.data.location_id) {
        // SUCCESS: Store location silently
        const locationData = {
          id: result.data.location_id,
          location: result.data.location,
          location_code: result.data.location_code,
          color_code: result.data.color_code,
          box_id: result.data.box_id,
          matched_field: result.data.matched_field,
          allow_multiple_orders: result.data.allow_multiple_orders,
          is_checkin_default: result.data.is_checkin_default,
        };

        this.setState({
          validatedLocation: locationData,
          isValidatingLocation: false,
          locationError: "",
        }, () => {
          // Auto-focus Save button if requested and enabled
          if (shouldAutoFocus) {
            const { prescriptionAssign } = this.props;
            const searchedRxItems = this.props.searchedRxItems || [];
            const itemCount = searchedRxItems.length;
            const isLocationValid = this.isLocationValid();
            const canAssign = prescriptionAssign && itemCount > 0 && isLocationValid;

            if (canAssign && this.saveButtonRef.current) {
              // Simple focus after state update
              setTimeout(() => {
                if (this.saveButtonRef.current) {
                  try {
                    this.saveButtonRef.current.focus();
                  } catch (error) {
                    console.error("[Counter] Error focusing save button:", error);
                  }
                }
              }, 150);
            }
          }
        });

        if (this.props.onLocationChange) {
          this.props.onLocationChange(locationData);
        }
      } else {
        // Check if location is occupied
        const errorMessage = result.data && result.data.is_occupied
          ? this.props.t("Assign.LocationOccupied", {
            defaultValue: "Location is already in use",
          })
          : this.props.t("Assign.InvalidLocation", {
            defaultValue: "Invalid location",
          });

        this.setState({
          validatedLocation: null,
          isValidatingLocation: false,
          locationError: errorMessage,
          locationTouched: true,
        });

        if (this.props.onLocationChange) {
          this.props.onLocationChange(null, { preserveInput: true });
        }
      }
    } catch (error) {
      console.error("Error validating location:", error);
      this.setState({
        validatedLocation: null,
        isValidatingLocation: false,
        locationError: this.props.t("Assign.InvalidLocation", {
          defaultValue: "Invalid location",
        }),
        locationTouched: true,
      });

      if (this.props.onLocationChange) {
        this.props.onLocationChange(null, { preserveInput: true });
      }
    }
  };

  handleLocationChange = (value) => {
    if (this.state.validatedLocation || this.state.locationError) {
      this.setState({
        validatedLocation: null,
        locationError: "",
      });
      if (this.props.onLocationChange) {
        this.props.onLocationChange(null, { preserveInput: true });
      }
    }

    if (this.props.onBinIdChange) {
      this.props.onBinIdChange(value);
    }
  };

  handleLocationSelect = (location) => {
    if (!location) {
      this.setState({
        validatedLocation: null,
        locationError: "",
        locationTouched: false,
        isValidatingLocation: false,
      });
      if (this.props.onLocationChange) {
        this.props.onLocationChange(null);
      }
      if (this.props.onBinIdChange) {
        this.props.onBinIdChange("");
      }
      return;
    }

    const locationData = {
      id: location.id,
      location: location.location,
      location_code: location.location_code,
      color_code: location.color_code,
      box_id: location.box_id || (this.props.currentBoxData && this.props.currentBoxData.id),
      matched_field: "location",
      allow_multiple_orders: location.allow_multiple_orders,
      is_checkin_default: location.is_checkin_default,
    };

    this.setState({
      validatedLocation: locationData,
      isValidatingLocation: false,
      locationError: "",
      locationTouched: false,
    });

    if (this.props.onBinIdChange) {
      this.props.onBinIdChange(locationData.location || "");
    }
    if (this.props.onLocationChange) {
      this.props.onLocationChange(locationData);
    }
  };
  handleLocationBlur = async () => {
    // Enable auto-focus on blur when validation succeeds
    await this.validateLocationAPI(true);
  };

  handleLocationKeyPress = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await this.validateLocationAPI(true); // Enable auto-focus on Enter key
    }
  };

  isLocationValid = () => {
    return this.state.validatedLocation !== null;
  };

  handleClear = () => {
    this.setState({
      locationError: "",
      locationTouched: false,
    });
    if (this.props.onClear) {
      this.props.onClear();
    }
  };

  handleAssign = () => {
    if (this.props.onAssign) {
      this.props.onAssign();
    }
  };

  handleAssignAndPrint = () => {
    if (this.props.onAssignAndPrint) {
      this.props.onAssignAndPrint();
    }
  };

  renderCollectDeliveryDialog = () => {
    const {
      collectDeliveryOpen,
      collectDeliveryUsers,
      collectDeliveryUserId,
      collectDeliveryBadge,
      collectDeliveryStockCode,
      collectDeliveryCart,
      collectDeliveryError,
      collectDeliveryLoading,
      collectDeliveryConfirming,
      collectDeliveryLocationTypeId,
    } = this.state;

    return (
      <CollectDeliveryDialog
        open={collectDeliveryOpen}
        users={collectDeliveryUsers}
        selectedUserId={collectDeliveryUserId}
        badgeInput={collectDeliveryBadge}
        stockCode={collectDeliveryStockCode}
        cart={collectDeliveryCart}
        error={collectDeliveryError}
        loading={collectDeliveryLoading}
        confirming={collectDeliveryConfirming}
        onClose={this.closeCollectDeliveryDialog}
        onUserChange={this.handleCollectDeliveryUserChange}
        onBadgeInputChange={this.handleCollectDeliveryUserInputChange}
        onBadgeScan={this.handleCollectDeliveryBadgeScan}
        onStockCodeChange={this.handleCollectDeliveryStockCodeChange}
        onAddStock={this.addCollectDeliveryStockCode}
        onRemoveOrder={this.removeCollectDeliveryCartOrder}
        onConfirm={this.confirmCollectDelivery}
        locationLabel={getCollectDeliveryLocationLabel(
          collectDeliveryLocationTypeId || this.props.locationtype_id
        )}
        userOptional={
          (collectDeliveryLocationTypeId || this.props.locationtype_id) ===
          LOCATION_TYPE_ID.Bedside
        }
        t={this.props.t}
      />
    );
  };

  render() {
    const {
      t,
      // eslint-disable-next-line no-unused-vars
      classes,
      searchedRxItems = [],
      searchedItems = {},
      stockCode = "",
      pickupCode = "",
      prescriptionAssign = true,
      onRxChange,
      onQtyChange,
      onAmountDueChange,
      onControlledSubstanceChange,
      onConsultChange,
      rxNumber = "",
      binId = "",
      patientFirstName = "",
      patientLastName = "",
      patientEmail = "",
      patientEmailError = "",
      patientPhone = "",
      patientPhoneError = "",
      patientDOB = "",
      patientRecord = "",
      locationName = "",
      locationTypeDisplay = "",
      addressLine1 = "",
      addressLine2 = "",
      city = "",
      state = "",
      zip = "",
      orderTotal = 0,
      onPatientEmailChange,
      onPatientPhoneChange,
      locationtype_id,
      locationtype,
      onLocationTypeChange,
      boxes = [],
      onCounterLocationChange,
      isNonRxDeliverEnabled = false,
      otcDeliveryEntitlement = null,
      otcDeliveryEntitlementLoading = false,
      nonRxCartItems = [],
      upcScan = '',
      upcScanBusy = false,
      onUpcScanChange,
      onUpcScanKeyDown,
      onUpcScanSubmit,
      onUpcScanFocus,
      onUpcScanBlur,
      onAddNonRxItem,
      onAddCustomNonRxItem,
      onChangeNonRxItem,
      onRemoveNonRxItem,
      onSearchNonRxCatalog,
      onSelectNonRxCatalogProduct,
      onSaveNonRxProductImage,
      roomNumber = "",
      onRoomNumberChange,
      locationOptions = [],
      isLoadingLocationOptions = false,
    } = this.props;

    const {
      counterOrders,
      popupData,
      deletepopup,
      printDialogOpen,
      open,
      loader,
      loadingCounterOrders,
      isPatientVerificationOpen,
      patientVerifyName,
      patientVerifyDob,
      patientVerifyError,
      isSubmittingPatientVerification,
      isUpdatingPaymentStatus,
      paymentStatusError,
      locationError,
      isConsult,
      isVideoConsultation,
      isConsultAcceptedCall,
      isPharmacistConnected,
      startRinging,
      connectedPharmacistName,
      isAudioOnlyConsult,
      localVideoTrack,
      remoteVideoTrack,
    } = this.state;

    const itemCount =
      searchedRxItems && searchedRxItems.length ? searchedRxItems.length : 0;

    // Filter out processed orders (Completed, Returned, Cancelled) and sort
    const sortedCounterOrders =
      counterOrders && counterOrders.length > 0
        ? [...counterOrders]
          .filter((order) => {
            if (!order) return false;
            const typeId = order.type_id;
            // Filter out processed orders
            return (
              typeId !== DEVICE_TYPE.Completed &&
              typeId !== DEVICE_TYPE.Returned &&
              typeId !== DEVICE_TYPE.Cancelled
            );
          })
          .sort((a, b) =>
            compareOrders(a, b, DEVICE_TYPE, {
              isCurbside: locationtype_id === LOCATION_TYPE_ID.Curbside,
            })
          )
        : [];

    // Check if location is valid for button disabling
    const isLocationValid = this.isLocationValid();
    const canAssign = prescriptionAssign && itemCount > 0 && isLocationValid;

    const readyOrdersForCurrentPatient = this.getReadyOrdersForCurrentPatient();
    const showAddToButton = itemCount > 0 && readyOrdersForCurrentPatient.length > 0;

    return (
      <Box sx={{
        width: "100%",
        p: 3,
        backgroundColor: "#f8fafc",
        height: "auto",
        maxHeight: "none",
        display: "block",
        flexDirection: 'row',
        overflow: "visible",
        overflowY: "visible",
        position: "relative",
      }}>
        {/* Arrived Notification Sound */}
        <ArrivedNotification />
        {this.renderCollectDeliveryDialog()}

        <PatientVerificationDialog
          open={isPatientVerificationOpen}
          t={t}
          nameValue={patientVerifyName}
          dobValue={patientVerifyDob}
          onNameChange={this.handlePatientVerifyNameChange}
          onDobChange={this.handlePatientVerifyDobChange}
          onCancel={this.closePatientVerificationDialog}
          onSubmit={this.submitPatientVerification}
          error={patientVerifyError}
          loading={isSubmittingPatientVerification}
        />

        {/* MultiPickupDialog - Always shown for all orders (single or multiple) */}
        {open && (
          <MultiPickupDialog
            open={open}
            hideOnPrint={this.props.locationtype_id === LOCATION_TYPE_ID.Bedside}
            onClose={this.handleClose}
            t={t}
            patientOrders={this.state.patientOrders}
            customerName={(popupData && popupData.customerName) || this.state.dialogCustomerName || ""}
            customerDob={(popupData && (popupData.dob_str || popupData.dob)) || this.state.dialogCustomerDob || ""}
            customerPhone={(popupData && popupData.phone) || this.state.dialogCustomerPhone || ""}
            customerEmail={(popupData && popupData.email) || this.state.dialogCustomerEmail || ""}
            popupData={popupData}
            consultationNotes={this.state.consultationNotes || ""}
            DEVICE_TYPE={DEVICE_TYPE}
            orderBagScanStatus={this.state.orderBagScanStatus}
            onUpdateBagScanStatus={this.updateBagScanStatus}
            onTogglePaymentStatus={this.handleTogglePaymentStatus}
            isUpdatingPaymentStatus={isUpdatingPaymentStatus}
            paymentStatusError={paymentStatusError}
            onTerminalPayment={this.handleTerminalPayment}
            isProcessingTerminalPayment={this.state.isProcessingTerminalPayment}
            onPayrollDeduct={this.handlePayrollDeduct}
            isProcessingPayLater={this.state.isProcessingPayLater}
            onCashPayment={this.handleCashPayment}
            isProcessingCashPayment={this.state.isProcessingCashPayment}
            onCaptureSignature={this.handleSubmitSignature}
            onSubmitIdCapture={this.handleSubmitIdCapture}
            onSaveConsultation={this.handleSaveConsultation}
            isSavingConsultation={this.state.isSavingConsultation}
            onDeclineConsultation={this.handleDeclineConsultation}
            isDecliningConsultation={this.state.isDecliningConsultation}
            consultationError={this.state.consultationError}
            onScanId={this.handleScanId}
            isScanningId={this.state.isScanningId}
            scanIdError={this.state.scanIdError}
            onBatchComplete={this.handleBatchComplete}
            onCancelOrder={this.handleCancelOrder}
            onReminderOrder={this.reminderHandler}
            onReprintOrder={this.handleReprintOrder}
            onReturnOrder={this.handleReturnOrder}
            prescriptionHoldResume={this.props.prescriptionHoldResume}
            onHoldOrder={this.handleHoldOrder}
            onReleaseOrder={this.handleReleaseOrder}
            onUpdateOrderDueAmount={this.updateOrderDueAmount}
            orderCompletionStatus={this.state.orderCompletionStatus}
            orderFullDetailsMap={this.state.orderFullDetailsMap}
            selectedOrderId={this.state.id}
            loading={loader}
            onStartConsult={this.handleStartConsult}
            isStartingConsultation={this.state.isStartingConsultation}
            accountDetails={this.props.accountDetails}
            enableTrim={this.state.enableTrim}
            trimChars={this.state.trimChars}
            paymentDeviceSelector={this.props.paymentDeviceSelector}
          />
        )}

        {/* Video Consultation Dialog */}
        {isVideoConsultation && (
          <VideoConsultation
            open={isConsult}
            onClose={this.handleCloseConsultation}
            t={t}
            localVideoTrack={localVideoTrack}
            remoteVideoTrack={remoteVideoTrack}
            isPharmacistConnected={isPharmacistConnected}
            isConsultAcceptedCall={isConsultAcceptedCall}
            startRinging={startRinging}
            connectedPharmacistName={connectedPharmacistName}
            isAudioOnlyConsult={isAudioOnlyConsult}
            onEndConsultation={this.handleEndConsultation}
            isEndingConsultation={false}
          />
        )}


        {/* Cancel Confirmation Dialog */}
        <CancelDialog
          open={deletepopup}
          onConfirm={this.cancelHandlerOrder}
          onClose={this.handleCloseCancel}
          t={t}
        />

        {/* Print Label Dialog */}
        <PrintDialog
          open={printDialogOpen}
          popupData={popupData}
          onLabelRef={(el) => (this.componentRef = el)}
        />

        {/* Add to existing order (Ready bag) dialog */}
        <AddToOrderDialog
          open={this.state.addToDialogOpen}
          onClose={this.handleCloseAddToDialog}
          readyOrders={readyOrdersForCurrentPatient}
          selectedOrderId={this.state.addToSelectedOrderId}
          onSelectOrder={this.handleAddToSelectOrder}
          onAdd={(orderId) => this.callAddToOrderApi(orderId, false)}
          onAddAndSave={(orderId) => this.callAddToOrderApi(orderId, true)}
          loading={this.state.addToSubmitting}
          identifyByStockCode={this.identifiesAddToByStockCode()}
          enableTrim={this.state.enableTrim}
          trimChars={this.state.trimChars}
          t={t}
        />

        {/* Header with Action Buttons */}
        {(
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            {locationtype && locationtype.length > 1 && onLocationTypeChange && (
              <DeliveryTypeTabs
                selectedLocationTypeId={locationtype_id}
                onLocationTypeChange={onLocationTypeChange}
                locationtype={locationtype}
                position="inline"
              />
            )}
            <Box sx={{ display: "flex", gap: 1.5 }}>
            {showAddToButton && (
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={this.handleOpenAddToDialog}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: "#059669",
                  color: "#059669",
                  borderWidth: 2,
                  backgroundColor: "rgba(5, 150, 105, 0.05)",
                  "&:hover": {
                    borderColor: "#047857",
                    backgroundColor: "rgba(5, 150, 105, 0.1)",
                    borderWidth: 2,
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px 0 rgba(5, 150, 105, 0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("Console.AddToOrder.Button", { defaultValue: "Add to" })}
              </Button>
            )}
              <Button
                ref={this.saveButtonRef}
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={this.handleAssign}
                disabled={!canAssign}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  borderWidth: 2,
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  "&:hover": {
                    borderColor: "#6d28d9",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    borderWidth: 2,
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:focus": {
                    outline: "none",
                    border: "3px solid #7c3aed",
                    borderColor: "#7c3aed",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.3), 0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    border: "3px solid #7c3aed",
                    borderColor: "#7c3aed",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.3), 0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:disabled": {
                    borderColor: "#b0bec5",
                    color: "#b0bec5",
                    backgroundColor: "transparent",
                    borderWidth: 2,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("Assign.SaveButton")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={this.handleAssignAndPrint}
                disabled={!canAssign}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  borderWidth: 2,
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  "&:hover": {
                    borderColor: "#6d28d9",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    borderWidth: 2,
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:focus": {
                    outline: "none",
                    border: "3px solid #7c3aed",
                    borderColor: "#7c3aed",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.3), 0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    border: "3px solid #7c3aed",
                    borderColor: "#7c3aed",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.3), 0 4px 12px 0 rgba(124, 58, 237, 0.2)",
                  },
                  "&:disabled": {
                    borderColor: "#b0bec5",
                    color: "#b0bec5",
                    backgroundColor: "transparent",
                    borderWidth: 2,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("Assign.SavePrintButton")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={this.handleClear}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  borderWidth: 2,
                  "&:hover": {
                    borderColor: "#6d28d9",
                    backgroundColor: "rgba(124, 58, 237, 0.1)",
                    borderWidth: 2,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("Assign.ClearButton")}
              </Button>
            </Box>
          </Box>
        )}

        <Grid
          container
          spacing={2}
          sx={{
            height: "auto",
            maxHeight: "none",
            flex: "none",
            minHeight: 0,
            overflow: "visible",
          }}
        >
          {/* Left Section - Patient Info & Orders */}
          {(
            <Grid item xs={12} md={7}>
            <ScanSection
              t={t}
              prescriptionAssign={prescriptionAssign}
              rxNumber={rxNumber}
              scanInputResetKey={this.state.scanInputResetKey}
              onScanChange={this.handleScanChange}
              scanRxInputRef={this.scanRxInputRef}
              binId={binId}
              onBinIdChange={this.handleLocationChange}
              onLocationBlur={this.handleLocationBlur}
              onLocationKeyPress={this.handleLocationKeyPress}
              onLocationSelect={this.handleLocationSelect}
              onLocationOptionsOpen={this.props.onLocationOptionsOpen}
              locationOptions={locationOptions}
              isLoadingLocationOptions={isLoadingLocationOptions}
              locationError={locationError}
              isValidatingLocation={this.state.isValidatingLocation}
              validatedLocation={this.state.validatedLocation}
              enableTrim={this.state.enableTrim}
              trimChars={this.state.trimChars}
              isEpicHasValidRx={this.props.isEpicHasValidRx}
              isEpicHasValidRxMessage={this.props.isEpicHasValidRxMessage}
              isNonRxDeliverEnabled={isNonRxDeliverEnabled}
              otcDeliveryEntitlementLoading={otcDeliveryEntitlementLoading}
              showNonRxScan={false}
              upcScan={upcScan}
              upcScanBusy={upcScanBusy}
              onUpcScanChange={onUpcScanChange}
              onUpcScanKeyDown={onUpcScanKeyDown}
              onUpcScanSubmit={onUpcScanSubmit}
              showRoomNumber={locationtype_id === LOCATION_TYPE_ID.Bedside}
              showLocationDropdown={
                locationtype_id === LOCATION_TYPE_ID.Bedside ||
                locationtype_id === LOCATION_TYPE_ID.Curbside
              }
              showOrderNotes={
                locationtype_id === LOCATION_TYPE_ID.Bedside ||
                locationtype_id === LOCATION_TYPE_ID.Curbside
              }
              roomNumber={roomNumber}
              onRoomNumberChange={onRoomNumberChange}
              orderNotes={
                locationtype_id === LOCATION_TYPE_ID.Bedside ||
                locationtype_id === LOCATION_TYPE_ID.Curbside
                  ? (this.props.orderNotes || '')
                  : ''
              }
              onOrderNotesChange={this.props.onOrderNotesChange}
            />

              <PatientInfoCard
                t={t}
                prescriptionAssign={prescriptionAssign}
                patientFirstName={patientFirstName}
                patientLastName={patientLastName}
                patientEmail={patientEmail}
                patientEmailError={patientEmailError}
                patientPhone={patientPhone}
                patientPhoneError={patientPhoneError}
                patientDOB={patientDOB}
                patientRecord={patientRecord}
                searchedItems={searchedItems}
                onPatientEmailChange={onPatientEmailChange}
                onPatientPhoneChange={onPatientPhoneChange}
              />

              <PrescriptionItemsTable
                t={t}
                prescriptionAssign={prescriptionAssign}
                searchedRxItems={searchedRxItems}
                onRxChange={onRxChange}
                onQtyChange={onQtyChange}
                onAmountDueChange={onAmountDueChange}
                onControlledSubstanceChange={onControlledSubstanceChange}
                onConsultChange={onConsultChange}
                onRemoveItem={this.handleRemoveRxItem}
              />

                {otcDeliveryEntitlementLoading ? (
                <div className="assign-marketplace-panel is-loading">
                  <div className="assign-marketplace-panel-header">
                    {t('Assign.Marketplace.SectionTitle', {
                      defaultValue: 'Marketplace',
                    })}
                  </div>
                  <div className="assign-non-rx-feature is-loading">
                    <div className="assign-non-rx-loading-header">
                      {t('Assign.NonRxCart.Title', {
                        defaultValue: 'Marketplace Items',
                      })}
                    </div>
                    <LinearProgress className="assign-non-rx-loading-bar" />
                    <p className="assign-non-rx-loading-copy">
                      {t('Assign.NonRxCart.CheckingAvailability', {
                        defaultValue: 'Checking Marketplace availability…',
                      })}
                    </p>
                  </div>
                </div>
              ) : null}
                {isNonRxDeliverEnabled && !otcDeliveryEntitlementLoading && (
                <div className="assign-marketplace-panel">
                  <div className="assign-marketplace-panel-header">
                    {t('Assign.Marketplace.SectionTitle', {
                      defaultValue: 'Marketplace',
                    })}
                    <span className="assign-marketplace-panel-badge">
                      {t('Assign.Marketplace.SectionBadge', {
                        defaultValue: 'Non-pharmacy items',
                      })}
                    </span>
                  </div>
                  <div className="assign-marketplace-scan">
                    <MarketplaceUpcScan
                      t={t}
                      variant="outlined"
                      value={upcScan}
                      disabled={!prescriptionAssign}
                      busy={upcScanBusy}
                      loading={false}
                      enabled={isNonRxDeliverEnabled}
                      onChange={onUpcScanChange}
                      onFocus={onUpcScanFocus}
                      onBlur={onUpcScanBlur}
                      onKeyDown={onUpcScanKeyDown}
                      onSubmit={onUpcScanSubmit}
                    />
                  </div>
                  <NonRxCart
                    t={t}
                    items={nonRxCartItems}
                    disabled={!prescriptionAssign}
                    onAddItem={onAddNonRxItem}
                    onAddCustomItem={onAddCustomNonRxItem}
                    onChangeItem={onChangeNonRxItem}
                    onRemoveItem={onRemoveNonRxItem}
                    onSearchCatalog={onSearchNonRxCatalog}
                    onSelectCatalogProduct={onSelectNonRxCatalogProduct}
                    onSaveProductImage={onSaveNonRxProductImage}
                  />
                </div>
              )}
              {!isNonRxDeliverEnabled && !otcDeliveryEntitlementLoading && (
                <div className="assign-marketplace-panel is-unavailable">
                  <div className="assign-marketplace-panel-header">
                    {t('Assign.Marketplace.SectionTitle', {
                      defaultValue: 'Marketplace',
                    })}
                  </div>
                  <div className="assign-non-rx-feature is-unavailable">
                  <OtcDeliveryCallout
                    t={t}
                    entitlement={
                      otcDeliveryEntitlement || {
                        feature_key: 'otc_delivery',
                        title: 'Marketplace',
                        enabled: false,
                        reason: 'locked',
                        talk_to_expert: true,
                      }
                    }
                    boxId={this.props.boxId}
                  />
                  <NonRxCart
                    t={t}
                    items={[]}
                    disabled
                    allowAdd={false}
                    onAddItem={() => {}}
                    onAddCustomItem={() => {}}
                    onChangeItem={() => {}}
                    onRemoveItem={() => {}}
                  />
                  </div>
                </div>
              )}

              {/* Order Total */}
              <Divider sx={{ my: 3, borderColor: "#e2e8f0" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  p: 2,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
                  borderRadius: 2,
                  border: "2px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mr: 2,
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#1e293b",
                  }}
                >
                  {t("Assign.AmountDue")}:
                </Typography>
                <TextField
                  size="small"
                  value={orderTotal}
                  disabled
                  variant="outlined"
                  sx={{
                    width: 140,
                    "& .MuiInputBase-root": {
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#7c3aed",
                      backgroundColor: "#ffffff",
                      borderRadius: 2,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: 2,
                    },
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Right Section - Counter Table & Search */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 3,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                height: "fit-content",
                maxHeight: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                flex: "none",
                position: "relative",
              }}
            >
              <CounterSearchInput
                t={t}
                prescriptionAssign={prescriptionAssign}
                searchText={this.state.searchText}
                searchInputResetKey={this.state.scanInputResetKey}
                onChange={(value) => this.inputHandler("searchText", value)}
                onVerifyPatient={this.openPatientVerificationDialog}
                onEnter={this.state.strictPatientVerification ? this.handleSearchEnterStrict : undefined}
                onRefresh={this.handleRefreshCounterTable}
                onCollectDeliver={this.openCollectDeliveryDialog}
                showCollectDeliver={isCollectDeliveryLocationType(locationtype_id)}
                isRefreshing={loadingCounterOrders || this.state.loadingMoreCounter}
                strictPatientVerification={this.state.strictPatientVerification}
                enableTrim={this.state.enableTrim}
                trimChars={this.state.trimChars}
              />

              <CounterOrdersList
                t={t}
                sortedCounterOrders={sortedCounterOrders}
                fetchMoreCounterData={this.fetchMoreCounterData}
                hasMoreCounter={this.state.hasMoreCounter}
                handleOrderClick={this.handleOrderClick}
                getStatusText={getStatusText}
                DEVICE_TYPE={DEVICE_TYPE}
                isBedside={locationtype_id === LOCATION_TYPE_ID.Bedside}
                isCurbside={locationtype_id === LOCATION_TYPE_ID.Curbside}
              />

              {/* Delivery Summary Panel */}
              {(
                <DeliverySummaryPanel
                  t={t}
                  prescriptionAssign={prescriptionAssign}
                  locationType={locationTypeDisplay}
                  locationName={locationName}
                  stockCode={stockCode}
                  pickupCode={pickupCode}
                  searchedItems={searchedItems}
                  addressLine1={addressLine1}
                  addressLine2={addressLine2}
                  city={city}
                  state={state}
                  zip={zip}
                  validatedLocation={this.state.validatedLocation}
                  boxes={boxes}
                  locationtype_id={locationtype_id}
                  boxId={this.props.boxId}
                  onLocationChange={this.props.onLocationChange}
                  onCounterLocationChange={onCounterLocationChange}
                  expirationDays={
                    this.props.accountDetails &&
                    this.props.accountDetails.expiration_days
                  }
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

CounterAssignUI.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  searchedRxItems: PropTypes.array,
  searchedItems: PropTypes.object,
  stockCode: PropTypes.string,
  pickupCode: PropTypes.string,
  prescriptionAssign: PropTypes.bool,
  onRxChange: PropTypes.func,
  onQtyChange: PropTypes.func,
  onAmountDueChange: PropTypes.func,
  onControlledSubstanceChange: PropTypes.func,
  onConsultChange: PropTypes.func,
  onAssign: PropTypes.func,
  onAssignAndPrint: PropTypes.func,
  onClear: PropTypes.func,
  onOpenPrintDialogWithRxByBin: PropTypes.func,
  onBinIdChange: PropTypes.func,
  rxNumber: PropTypes.string,
  binId: PropTypes.string,
  patientFirstName: PropTypes.string,
  patientLastName: PropTypes.string,
  patientEmail: PropTypes.string,
  patientPhone: PropTypes.string,
  patientEmailError: PropTypes.string,
  patientPhoneError: PropTypes.string,
  patientDOB: PropTypes.string,
  patientRecord: PropTypes.string,
  locationName: PropTypes.string,
  locationTypeDisplay: PropTypes.string,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  orderTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleLoader: PropTypes.func,
  onPatientEmailChange: PropTypes.func,
  onPatientPhoneChange: PropTypes.func,
  currentBoxData: PropTypes.object,
  accountDetails: PropTypes.object,
  courier_tracking: PropTypes.string,
  default_courier_selected: PropTypes.string,
  onRemoveItem: PropTypes.func,
  onLocationChange: PropTypes.func, // Callback when location is validated
  onLocationOptionsOpen: PropTypes.func,
  locationOptions: PropTypes.array,
  isLoadingLocationOptions: PropTypes.bool,
  onCounterLocationChange: PropTypes.func, // Callback when counter location (box) is selected from dropdown
  isEpicHasValidRx: PropTypes.bool,
  isEpicHasValidRxMessage: PropTypes.string,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Optional: for Add-to matching when parent has it
  prescriptionHoldResume: PropTypes.bool,
  isNonRxDeliverEnabled: PropTypes.bool,
  otcDeliveryEntitlement: PropTypes.object,
  otcDeliveryEntitlementLoading: PropTypes.bool,
  boxId: PropTypes.string,
  nonRxCartItems: PropTypes.array,
  upcScan: PropTypes.string,
  upcScanBusy: PropTypes.bool,
  onUpcScanChange: PropTypes.func,
  onUpcScanKeyDown: PropTypes.func,
  onUpcScanSubmit: PropTypes.func,
  onUpcScanFocus: PropTypes.func,
  onUpcScanBlur: PropTypes.func,
  onAddNonRxItem: PropTypes.func,
  onAddCustomNonRxItem: PropTypes.func,
  onChangeNonRxItem: PropTypes.func,
  onRemoveNonRxItem: PropTypes.func,
  onSearchNonRxCatalog: PropTypes.func,
  onSelectNonRxCatalogProduct: PropTypes.func,
  onSaveNonRxProductImage: PropTypes.func,
  roomNumber: PropTypes.string,
  onRoomNumberChange: PropTypes.func,
  orderNotes: PropTypes.string,
  onOrderNotesChange: PropTypes.func,
  paymentDeviceSelector: PropTypes.node,
};

export default withStyles(styles)(withTranslation()(CounterAssignUI));



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/index.js